export const en = {
  // ─── Navigation ───────────────────────────────────────────────
  nav: {
    home: 'Home',
    calendar: 'Calendar',
    explore: 'Explore',
    tickets: 'Tickets',
    create: 'Create',
    createEvent: 'Create Event',
    myEvents: 'My Events',
    profile: 'Profile',
    tapIn: 'Tap In',
    account: 'Account',
    login: 'Login',
    signIn: 'Sign In',
    signOut: 'Sign Out',
    back: 'Back',
    expandSidebar: 'Expand sidebar',
    collapseSidebar: 'Collapse sidebar',
  },

  // ─── Page titles ──────────────────────────────────────────────
  pages: {
    auth: 'Sign In',
    calendar: 'Calendar',
    create: 'Create',
    myEvents: 'My Events',
    profile: 'Profile',
    event: 'Event',
    eventSettings: 'Event Settings',
    notifications: 'Notifications',
    editProfile: 'Edit Profile',
    ticket: 'Ticket',
    explore: 'Explore',
  },

  // ─── Auth ─────────────────────────────────────────────────────
  auth: {
    signIn: 'Sign In',
    signUp: 'Sign Up',
    welcomeBack: 'Welcome Back',
    createAccount: 'Create Your Account',
    signInSubtitle: 'Sign in to your account to continue',
    signUpSubtitle: 'Create a new account to get started',
    signUpMethodsLabel: 'Sign up using one of the methods below:',
    signInMethodsLabel: 'Sign in using one of the methods below:',
    orContinueWith: 'Or continue with',
    continueWithGoogle: 'Continue with Google',
    continueWithApple: 'Continue with Apple',
    continueWithWallet: 'Connect Wallet',
    alreadyHaveAccount: 'Already have an account?',
    linking: 'Linking...',
    connecting: 'Connecting...',
    linkGoogle: 'Link Google',
    linkApple: 'Link Apple',
    appleNotAvailable: 'Apple sign-in is not available yet',
    popupBlocked: 'Popup blocked. Please allow popups for this site.',
    unexpectedError: 'An unexpected error occurred',
    oauthFailed: 'OAuth authentication failed',
    initializing: 'Initializing...',
    walletUnavailable: 'Solana wallet is not available. Use a supported wallet extension or the mobile app.',
    walletAuthFailed: 'Wallet authentication failed',
    walletAuthError: 'Failed to authenticate with wallet',
    signingUp: 'Signing up...',
    signingIn: 'Signing in...',
    signUpWithWallet: 'Sign up with Wallet',
    signInWithWallet: 'Sign in with Wallet',
    missingOAuthToken: 'Missing OAuth token',
    authFailed: 'Authentication failed',
    sessionNotVerified: 'Session could not be verified. Please try signing in again.',
    completingSignIn: 'Completing sign in...',
    redirecting: 'Redirecting to sign in...',
    introBadge: 'No wallets. No hassle.',
    introHeadlineSignIn: 'Welcome back to the',
    introHeadlineSignUp: 'Join the',
    introHighlight: 'ecosystem',
    introBodySignIn: 'Sign in to see your tickets, upcoming events, and personalised recommendations. It all just works.',
    introBodySignUp: 'Create an account to RSVP to events, host your own, and earn ecosystem rewards — no crypto knowledge required.',
    introFeaturePasswordless: 'Passwordless login',
    introFeaturePasswordlessDesc: 'Sign in with Google or Apple — no passwords to remember.',
    introFeatureWallet: 'Wallet-optional',
    introFeatureWalletDesc: 'Advanced? Link a crypto wallet for optional features. Most people never need to.',
    termsAgreement: 'By continuing you agree to our',
    terms: 'Terms',
    and: 'and',
    privacy: 'Privacy Policy',
  },

  // ─── Explore ──────────────────────────────────────────────────
  explore: {
    title: 'Explore Events',
    searchPlaceholder: 'Search events by title or description...',
    search: 'Search',
    activeFilters: 'Active filters:',
    clearAll: 'Clear all',
    enterSearchTerm: 'Enter a search term or apply filters to find events',
    failedToSearch: 'Failed to search events.',
    retry: 'Retry',
    found_one: 'Found {{count}} event',
    found_other: 'Found {{count}} events',
    noResults: 'No events found',
    tryDifferent: 'Try different keywords or filters',
    backToHome: 'Back to Home',
    eventsIn: 'Events in {{location}}',
    filterCityPlaceholder: 'Filter by city...',
    filterLocationType: 'Location type',
    filterDaysAhead: 'Days ahead',
    filterAllTypes: 'All types',
    filterInPerson: 'In person',
    filterOnline: 'Online',
    filterHybrid: 'Hybrid',
    filterNext7Days: 'Next 7 days',
    filterNext14Days: 'Next 14 days',
    filterNext30Days: 'Next 30 days',
    filterNext90Days: 'Next 90 days',
    applyFilters: 'Apply filters',
  },

  // ─── Event Detail ────────────────────────────────────────────
  eventDetail: {
    notFound: 'Event Not Found',
    notFoundDescription: "The event you're looking for doesn't exist.",
    private: 'Private Event',
    privateDescription: 'This event is private. Sign in or use an invitation link to view it.',
    loadError: 'Could Not Load Event',
    loadErrorDescription: 'Something went wrong while loading this event. Please try again.',
    tryAgain: 'Try Again',
    goHome: 'Go Home',
    joinNow: 'Join Now \u2014 Event is Live',
  },

  // ─── My Events ───────────────────────────────────────────────
  myEvents: {
    title: 'My Events',
    subtitle: "Manage events you're organizing and attending",
    createEvent: 'Create Event',
    organizing: 'Organizing',
    attending: 'Attending',
    collaborating: 'Collaborating',
    tickets: 'Tickets',
    eventsOrganizing: "Events I'm Organizing",
    communityEventsHosting: "Community events I'm hosting",
    sideEvent: 'side event',
    upcoming: 'Upcoming',
    past: 'Past',
    noUpcoming: 'No upcoming community events',
    noPast: 'No past community events',
    coverImage: 'Cover image for {{title}}',
    nft: 'NFT',
    partOf: 'Part of',
    eventsAttending: "Events I'm Attending",
    communityEventsAttending: "Community events you're attending",
    eventsCollaborating: "Events I'm Collaborating On",
    organizingWithCount: 'Organizing ({{count}})',
    attendingWithCount: 'Attending ({{count}})',
    collaboratingWithCount: 'Collaborating ({{count}})',
    ticketsWithCount: 'Tickets ({{count}})',
    sideEventCount_one: '{{count}} side event',
    sideEventCount_other: '{{count}} side events',
    eventCount_one: '{{count}} event',
    eventCount_other: '{{count}} events',
    noEventsFound: 'No events found',
    noUpcomingEvents: 'No upcoming events found',
    noPastEvents: 'No past events found',
    collaborator: 'Collaborator',
  },

  // ─── Calendar ────────────────────────────────────────────────
  calendar: {
    title: 'Event Calendar',
    description: 'Discover upcoming events and browse by month, week, or day. Find events in your city or online.',
    eventTypes: 'Event Types',
    publicEvent: 'Public Event',
    privateEvent: 'Private Event',
    attendingConfirmed: 'Attending (Confirmed)',
    pendingApproval: 'Pending Approval',
    onWaitlist: 'On Waitlist',
    filters: 'Filters',
    done: 'Done',
    keyboardShortcuts: 'Keyboard Shortcuts',
    close: 'Close',
    previousPeriod: 'Previous period',
    nextPeriod: 'Next period',
    goToToday: 'Go to today',
    monthView: 'Month view',
    weekView: 'Week view',
    dayView: 'Day view',
    listView: 'List view',
    showShortcuts: 'Show shortcuts',
    closeEscape: 'Close / Escape',
    calendar: 'Calendar',
    today: 'Today',
    month: 'Month',
    week: 'Week',
    day: 'Day',
    list: 'List',
    keyboardShortcutsTitle: 'Keyboard shortcuts (?)',
    previous: 'Previous',
    allEvents: 'All Events',
    next: 'Next',
    noUpcomingEvents: 'No upcoming events',
    dateTbd: 'Date TBD',
    noEventsScheduled: 'No events scheduled',
    noEvents: 'No events',
    ended: 'Ended',
    filterCityPlaceholder: 'Enter city',
    filterCountryPlaceholder: 'Enter country',
    attendeeCount_one: '{{count}} attendee',
    attendeeCount_other: '{{count}} attendees',
    eventsOnDate: 'Events on {{date}}',
    dayModalDesc: 'View and interact with all events scheduled for this date',
    eventCount_one: '{{count}} event',
    eventCount_other: '{{count}} events',
    noEventsScheduledDay: 'No events scheduled for this day',
    hybridLocation: 'Hybrid • {{location}}',
    peopleGoing_one: '{{count}} person going',
    peopleGoing_other: '{{count}} people going',
  },

  // ─── Landing (logged-out marketing) ───────────────────────────
  landing: {
    nav: {
      ariaLabel: 'Landing navigation',
      mobileAriaLabel: 'Mobile landing navigation',
      openMenu: 'Open menu',
      closeMenu: 'Close menu',
      menuTitle: 'Navigation',
      explore: 'Explore',
      partners: 'Partners',
      ecosystem: 'Ecosystem',
      tapIn: 'Tap In',
      roadmap: 'Roadmap',
      signIn: 'Sign In',
      createEvent: 'Create Event',
    },
    hero: {
      metaTitle: 'Creatix — Event Hub & Ecosystem | Pilot in Jogja',
      metaDescription:
        'The first event ecosystem platform in Indonesia combining easy paid ticketing with transparent attendance proofs. Supported by Solana, Metaplex, and Superteam Indonesia. Experience full-service execution (Tap In) pilot in Yogyakarta & Surroundings.',
      wordmark: 'creatix',
      displayLine1: 'An event ecosystem',
      displayLine2: 'driven by networks, not ads.',
      drivenByPrefix: 'driven by',
      rotatorSuffix: ',',
      displaySuffix: 'not ads.',
      rotatorWords: ['networks', 'brands', 'creators', 'hubs'],
      subhead:
        "Indonesia's first event hub—a platform where organizers, communities, and brands grow together through mutually reinforcing networks. Sell tickets through payment gateways just like regular platforms—every ticket is secured with tamper-proof attendance proofs. No wallets. No hassle. Just secure event tickets.",
      subLine1:
        "Indonesia's first event hub—a platform where organizers, communities, and brands grow together through mutually reinforcing networks.",
      subLine2:
        'Sell tickets through Payment Gateways just like regular event platforms. Behind the scenes, every ticket is secured with tamper-proof attendance proofs. No crypto wallets. No hassle. Just secure event tickets.',
      searchPlaceholder: 'Search events, communities, cities, or hubs...',
      primaryCta: 'Join the Ecosystem',
      secondaryCta: 'Explore Events',
      slideshowAriaLabel: 'Hero event slideshow',
      supportedBy: 'Supported by global infrastructure',
      audiencePill: 'Built for organizers & communities',
      eyebrowKeywords: 'Event Hub · Network Growth · Tap In Pilot',
    },
    howItWorks: {
      label: 'How It Works',
      title: 'Creatix is not just a directory. It is a growth engine.',
      titleLine1: 'Creatix is not just a directory.',
      titleLine2: 'It is a growth engine.',
      titleHighlightPrefix: 'It is a',
      titleHighlight: 'growth engine',
      subtitle:
        'Every event you host strengthens the entire ecosystem—and the ecosystem promotes your event in return—at no extra cost.',
      rotatingLines: [
        'Every event you host strengthens the entire ecosystem—and the ecosystem promotes your event in return.',
        'Creatix is not just a directory. It is a growth engine.',
        'Organic cross-promotion across partners—at no extra cost.',
      ],
      step1: {
        title: 'Create Your Event (Free or Paid)',
        photoAlt: 'Organizer setting up an event on stage before doors open',
        platform: 'Comprehensive event management—QR check-in, automated attendee lists, real-time analytics dashboard.',
        ecosystem: 'Your event is instantly indexed and visible to all partners within the network hub.',
      },
      step2: {
        title: 'Connect to Hubs & Expand Reach',
        photoAlt: 'Attendees connecting and sharing at a community meetup',
        platform: 'Manage multiple co-hosts, speakers, and vendors from a single collaborative dashboard.',
        ecosystem:
          'Your event automatically gains organic cross-promotion across the audience base of all ecosystem partners—slashing paid ad spend.',
      },
      step3: {
        title: 'Scale & Choose Your Growth Path',
        photoAlt: 'Workshop activation with engaged participants',
        platform: 'Sell tickets directly through Creatix with a transparent platform fee (similar to Loket/Eventbrite). Free community events remain 100% free.',
        ecosystem:
          'Deliver memorable talk shows, workshops, or community meetups in our pilot operating regions—our team goes hands-on to handle full management.',
        autonomousPath: 'Autonomous Path',
        executionPath: 'Execution Path (Tap In)',
      },
    },
    featureTabs: {
      label: 'The Platform',
      titleLine1: 'One platform.',
      titleLine2: 'Full growth.',
      subtitle:
        'From your first event to a self-reinforcing network—Creatix covers every step.',
      tab1: {
        label: 'Create',
        title: 'Create Your Event',
        body: 'Free or paid, online or in-person. QR check-in, automated attendee lists, and a real-time analytics dashboard are built in—no extra tools to wire together.',
      },
      tab2: {
        label: 'Connect',
        title: 'Connect to Hubs',
        body: 'Co-hosts, speakers, and ecosystem partners on one collaborative dashboard. Your event is indexed and organically cross-promoted across every partner audience.',
      },
      tab3: {
        label: 'Scale',
        title: 'Scale Your Growth',
        body: 'Sell tickets through familiar payment gateways while every ticket is silently secured with tamper-proof attendance proofs. Free community events stay 100% free.',
      },
    },
    socialProof: {
      label: 'Social Proof',
      title: "Creatix is where Indonesia's dynamic event ecosystem meets.",
      tagline: 'Needed and Trusted by Organizers, Communities, and Brands.',
      titleLine1: 'Needed and Trusted',
      titleLine2: 'by Organizers, Communities, and Brands.',
      supportingText:
        'From free community meetups to large-scale brand activations—Creatix is where Indonesia\'s dynamic event ecosystem meets.',
      rotatingLines: [
        'Needed and Trusted by Organizers, Communities, and Brands.',
        'From free community meetups to large-scale brand activations.',
        'Creatix is where Indonesia\'s dynamic event ecosystem meets.',
      ],
      metric: {
        eventsHosted: '50+',
        eventsHostedLabel: 'Events',
        activePartners: '20+',
        activePartnersLabel: 'ecosystem event partners',
        verifiedUsers: '1,200+',
        verifiedUsersLabel: 'active users',
      },
      andManyMore: 'and many more',
    },
    tapIn: {
      statusBadge: 'Limited Pilot Phase — Yogyakarta & Surroundings',
      tagline: 'Creatix Tap In — Full Service Execution',
      title: 'We hit the ground. Starting from Jogja.',
      titleLine1: 'We hit the ground.',
      titleLine2: 'Starting from Jogja.',
      subtitle:
        'The Creatix team is physically present in Yogyakarta to execute top-tier event infrastructure—turning passive participants into active contributors. We collaborate with local talent to deliver high-quality talk shows, community meetups, workshops, and social & sport events.',
      rotatingLines: [
        'Creatix Tap In — Full Service Execution',
        'We hit the ground. Starting from Jogja.',
        'Turning passive participants into active contributors.',
      ],
      photoAlt: 'Creatix ground team facilitating a workshop in Yogyakarta',
      photoCaption: 'Ground team · Jogja pilot',
      services: {
        colHeaders: {
          type: 'Event Type',
          team: 'Ground Team',
          outcome: 'Real Outcomes for Clients',
        },
        talkshow: {
          type: 'Talkshow & Panel',
          team: 'Creatix Team + Local Jogja EO',
          outcome:
            'Events executed with precision, educated audience, transparent reports, and comprehensive documentation.',
        },
        meetup: {
          type: 'Community Meetup',
          team: 'End-to-End Full Coordination',
          outcome: 'Local communities cross-connect, elevated brand visibility, and measurable network impact.',
        },
        workshop: {
          type: 'Workshop & Social/Sport',
          team: 'Local Experts & Facilitators',
          outcome: 'Authentic interactive experiences for participants and a high-quality portfolio for partners.',
        },
      },
      whatsappCta: 'Contact Creatix Jogja Team',
      form: {
        title: 'Request Tap In Execution',
        name: 'Name',
        email: 'Email',
        communityOrBrand: 'Community / Brand',
        eventCategory: 'Event Category',
        eventCategoryOptions: {
          talkshow: 'Talkshow & Panel',
          meetup: 'Community Meetup',
          workshop: 'Workshop & Social/Sport',
          social_sport: 'Social & Sport Event',
        },
        estimatedDate: 'Estimated Date',
        description: 'Event Plan Description',
        submit: 'Submit Inquiry',
        successTitle: 'Inquiry sent',
        successDesc: 'The Creatix team will contact you soon.',
        failed: 'Could not submit inquiry. Please try again.',
        nameRequired: 'Name is required',
        communityRequired: 'Community or brand name is required',
        emailInvalid: 'Enter a valid email address',
        categoryRequired: 'Select an event category',
      },
    },
    coreValues: {
      label: 'Core Values',
      title: 'What we stand for',
      titleLine1: 'What we',
      titleLine2: 'stand for',
      titleHighlight: 'built for growth',
      titleSuffix: '—not gatekeeping.',
      rotatingLines: [
        'Open & borderless. No closed-door bureaucracy.',
        'Verifiable credibility with Web2-simple UX.',
        'Network over platform—you are a stakeholder.',
      ],
      openBorderless: {
        title: 'Open & Borderless',
        desc: 'No closed-door bureaucracy. The ecosystem application form is always transparently open to anyone who wants to contribute.',
      },
      onChainCredibility: {
        title: 'Verifiable Credibility',
        desc: 'Every ticket is secured as a high-speed digital asset. Proof of attendance is permanent, anti-counterfeit, and system-validated.',
      },
      intuitiveWeb2: {
        title: 'Intuitive Web2 UX',
        desc: 'Instant transactions using GoPay, OVO, or QRIS. Blockchain infrastructure runs automatically on the backend. Users focus entirely on the event experience.',
      },
      networkOverPlatform: {
        title: 'Network Over Platform',
        desc: 'The value increases exponentially as more partners join. You are not just a consumer, but a stakeholder in the ecosystem.',
      },
    },
    roadmap: {
      label: 'Roadmap',
      title: 'Product line roadmap',
      titleLine1: 'Product line',
      titleLine2: 'roadmap',
      rotatingLines: [
        'Event Hub & Ecosystem Network — live today.',
        'Tap In ground execution — piloting in Jogja.',
        'Crowdfunding architecture — research phase underway.',
      ],
      statusLive: 'Live',
      statusLiveAria: 'Live',
      statusPilot: 'Pilot · Yogyakarta',
      statusPilotAria: 'Pilot in Yogyakarta',
      statusInDevelopment: 'In development',
      statusInDevelopmentAria: 'In development',
      statusSoon: 'Soon',
      statusSoonAria: 'Coming soon',
      pl01: {
        label: 'PL 01: Event Hub & Ecosystem Network',
        title: 'Event Hub & Ecosystem Network',
        status: 'live',
        progress: '100',
        desc: 'Managing 50+ events, 20+ ecosystem event partners, and facilitating over 1,200 active users.',
      },
      pl02: {
        label: 'PL 02: Tap In — Ground Execution Services',
        title: 'Tap In — Ground Execution Services',
        status: 'pilot',
        progress: '80',
        desc: 'Focusing on physical management of talkshows, meetups, workshops, and social & sports activations in Jogja.',
      },
      pl03: {
        label: 'PL 03: Paid Ticketing (Hybrid + Web2 Gateway)',
        title: 'Paid Ticketing (Hybrid + Web2 Gateway)',
        status: 'development',
        progress: '60',
        desc: 'Deep integration with Midtrans/Xendit for seamless backend ticket minting.',
      },
      pl04: {
        label: 'PL 04: Community-Based Crowdfunding',
        title: 'Community-Based Crowdfunding',
        status: 'soon',
        progress: '0',
        desc: 'Research phase for secure decentralized fundraising architecture.',
      },
    },
    ctaBand: {
      headline: 'Ready to build your event ecosystem?',
      headlineLine1: 'Ready to build your',
      headlineLine2: 'event ecosystem?',
      eyebrow: 'Free to join',
      subhead:
        'Join our network hub for free. Bring your event, grow your audience, and harness the power of organic cross-promotion.',
      rotatingLines: [
        'Join our network hub for free.',
        'Bring your event. Grow your audience.',
        'Harness organic cross-promotion across the ecosystem.',
      ],
      primaryCta: 'Join the Ecosystem — Free Now',
      feeTransparency:
        'Free events have 0% platform fees. Paid events are subject to a small, transparent, flat platform fee similar to Loket/Eventbrite with no monthly commitments.',
    },
    pages: {
      tapIn: {
        metaTitle: 'Tap In — Full Service Event Execution | Creatix',
        kicker: 'Ground execution',
        title: 'Ground team, full-service execution.',
        titleLine1: 'Ground team,',
        titleLine2: 'full-service execution.',
        subtitle:
          'The Creatix team is physically present in Yogyakarta to execute top-tier event infrastructure—turning passive participants into active contributors.',
        cta: 'Request Tap In Execution',
      },
      roadmap: {
        metaTitle: 'Product Roadmap | Creatix',
        kicker: 'Roadmap',
        title: 'Building Indonesia\'s event ecosystem, phase by phase.',
        titleLine1: 'Building Indonesia\'s event ecosystem,',
        titleLine2: 'phase by phase.',
        subtitle:
          'From live event hub to Tap In ground execution, paid ticketing, and community crowdfunding research.',
        cta: 'Join the Ecosystem',
      },
      ecosystem: {
        metaTitle: 'Ecosystem Partners | Creatix',
        kicker: 'Ecosystem Partners',
        title: 'Grow with the network—not against it.',
        titlePrefix: 'Grow with',
        rotatorWords: ['the network', 'communities', 'brands', 'creators'],
        titleSuffix: '—not against it.',
        subtitle:
          "Every partner joining Creatix amplifies the entire ecosystem. Cross-promotion is organic and at no extra cost. You aren't just a user—you're a stakeholder.",
        primaryCta: 'Apply as Partner',
        secondaryCta: 'Explore Events',
        infraKicker: 'Global Infrastructure',
        infraTitle: 'Built on rails you can',
        infraHighlight: 'trust',
        partnersKicker: 'Ecosystem Partners',
        partnersTitle: 'The community powering',
        partnersHighlight: 'every event',
        ctaKicker: 'Open ecosystem',
        ctaTitle: 'Become an ecosystem partner.',
        ctaBody:
          'The ecosystem application form is transparently open to anyone who wants to contribute—no closed-door bureaucracy.',
        ctaApply: 'Apply Now',
      },
    },
  },

  // ─── Landing footer (logged-out marketing only) ───────────────
  landingFooter: {
    ariaLabel: 'Marketing footer',
    description: 'The Events Hub for communities. Discover events, register, get QR tickets, and check in — built for communities and organizers.',
    platform: 'Platform',
    support: 'Support',
    explore: 'Explore Events',
    calendar: 'Calendar',
    createEvent: 'Create Event',
    howItWorks: 'How It Works',
    tapIn: 'Tap In',
    roadmap: 'Roadmap',
    contact: 'Contact Us',
    privacy: 'Privacy Policy',
    terms: 'Terms of Service',
    xAria: 'X (Twitter)',
    instagramAria: 'Instagram',
    emailAria: 'Email',
    copyright: '© 2026 Creatix. All rights reserved.',
    tagline: 'Made for the events ecosystem.',
  },

  // ─── Home (logged-in events feed) ─────────────────────────────
  home: {
    greeting: 'Good {{period}}, {{name}}',
    greetingSubtitle: "Here's what's coming up for you",
    greetingPeriod: {
      morning: 'morning',
      afternoon: 'afternoon',
      evening: 'evening',
    },
    yourEvents: 'Your events',
    yourEventsSubtitle: "Events you're organizing or attending",
    viewMyEvents: 'View all',
    discover: 'Discover',
    discoverSubtitle: "See what's happening and what's coming up",
    events: 'Events',
    eventsSubtitle: "See what's happening and what's coming up",
    viewCalendar: 'View Calendar',
    failedToLoad: 'Failed to load events. Please try again later.',
    happeningNow: 'Happening Now',
    eventsLive_one: '{{count}} event live',
    eventsLive_other: '{{count}} events live',
    startingSoon: 'Starting Soon',
    eventsStarting_one: '{{count}} event about to start',
    eventsStarting_other: '{{count}} events about to start',
    todayEvents: 'Today',
    eventsToday_one: '{{count}} event',
    eventsToday_other: '{{count}} events',
    upcomingEvents: 'Upcoming Events',
    upcoming: 'Upcoming',
    showMoreEvents: 'Show more events',
    loadMoreEvents: 'Load more events',
    viewFullCalendar: 'View full calendar',
    noEvents: 'No events found',
    noUpcomingYet: 'No upcoming events yet',
    noEventsSubtitle: 'Be the first to create an event',
    viewAll: 'View all',
    liveNow: 'Live Now',
    live: 'LIVE',
    inTime: 'In {{time}}',
    timeRemaining: '{{time}} remaining',
    startsIn: 'Starts in {{time}}',
  },

  // ─── Event Detail ─────────────────────────────────────────────
  event: {
    rsvp: 'RSVP',
    cancelRsvp: 'Cancel RSVP',
    confirmed: 'Confirmed',
    pending: 'Pending Approval',
    waitlisted: 'On Waitlist',
    cancelled: 'Cancelled',
    checkedIn: 'Checked In',
    attending: 'Attending',
    waitlistPosition: 'Waitlist #{{position}}',
    capacity: 'Capacity',
    spotsLeft: '{{count}} spot left',
    spotsLeft_other: '{{count}} spots left',
    soldOut: 'Sold Out',
    free: 'Free',
    inPerson: 'In-Person',
    online: 'Online',
    hybrid: 'Hybrid',
    share: 'Share',
    addToCalendar: 'Add to Calendar',
    viewOnMap: 'View on Map',
    attendees: 'Attendees',
    schedule: 'Schedule',
    community: 'Community',
    about: 'About',
    organizer: 'Organizer',
    coHosts: 'Co-hosts',
    sessions: 'Sessions',
    hostedBy: 'Hosted by',
    live: 'LIVE',
    inTime: 'In {{time}}',
    timeRemaining: '{{time}} remaining',
    happeningNow: 'Happening now',
    startsIn: 'Starts in {{time}}',
    locationAfterRsvp: 'Location available after RSVP',
    hybridAttendance: '{{inPerson}} in-person · {{online}} online',
    attendee_one: '{{count}} attendee',
    attendee_other: '{{count}} attendees',
    attendeeWithCapacity: '{{count}} / {{capacity}} attending',
    ticketsSold: 'Tickets Sold',
    rsvps: 'RSVPs',
    viewEventAria: 'View {{title}}',
    rsvpToEvent: 'RSVP to this event',
    spotsFilled: '{{count}} of {{capacity}} spots filled',
    peopleGoing: '{{count}} people going',
    rsvpNow: 'RSVP Now',
    ticketsNotAvailable: 'Tickets are not yet available for this event.',
    eventDetails: 'Event Details',
    endsAt: 'Ends: {{date}}',
    joinLink: 'Join Link',
    person: 'person',
    people: 'people',
    eventCancelled: 'Event Cancelled',
    collaborators: 'Collaborators',
    collaborator: 'Collaborator',
    joinMeeting: 'Join Meeting',
    linkAvailableBeforeStart: 'Available 1 hour before start',
    linkTba: 'Link to be announced',
    when: 'When',
    where: 'Where',
    venueTba: 'Venue TBA',
    locationAfterConfirmation: 'Location available after confirmation',
    fullAddressAfterRsvp: 'Full address available after RSVP confirmation',
    onlineAccess: 'Online Access',
    goingCount: '{{count}} going',
    totalSpots: '{{count}} total spots',
    openEntry: 'Open Entry',
    attendingCount: '{{count}} attending',
    shareText: 'Check out this event: {{title}}',
    showQrCode: 'Show QR Code',
    shareOnX: 'Share on X',
    shareOnFacebook: 'Share on Facebook',
    shareOnLinkedIn: 'Share on LinkedIn',
    copyLink: 'Copy Link',
    qrCodeTitle: 'Event QR Code',
    qrCodeAlt: 'Event QR Code',
    qrCodeDesc: 'Scan this code to quickly share this event with others.',
    downloadQr: 'Download QR',
    downloadQrHint: 'Save as SVG for high quality',
    selectTicket: 'Select Your Ticket',
    selectTicketDesc: 'Select a ticket tier to continue.',
    availableTickets: 'Available Tickets',
    chooseTicketDesc: 'Choose your ticket type and secure your spot',
    hybridBanner: 'This is a hybrid event',
    hybridBannerDesc: 'Attend in-person at the venue OR join online from anywhere.',
    whatDoesThisMean: 'What does this mean?',
    ticketsComingSoon: 'Tickets Coming Soon',
    ticketsComingSoonDesc: "The host hasn't released tickets for this event yet. Check back soon!",
    sessionCount_one: '{{count}} session',
    sessionCount_other: '{{count}} sessions',
    viewFullSchedule: 'View the full schedule',
    manageProgramme: 'Manage Programme',
    viewSchedule: 'View Schedule',
    viewCommunity: 'View Community →',
    xVerified: 'X verified',
    xLinked: 'X linked',
    getTicket: 'Get Ticket',
    getTierCta: 'Get {{tierName}} · {{type}} — {{price}}',
    gallery: 'Gallery',
    promoImageAlt: '{{title}} promotional image',
    previousImageAria: 'Previous image',
    nextImageAria: 'Next image',
  },

  // ─── Event Creation ───────────────────────────────────────────
  createEvent: {
    title: 'Create Event',
    basicInfo: 'Basic Info',
    media: 'Media',
    schedule: 'Schedule & Location',
    tickets: 'Tickets',
    registration: 'Registration',
    eventTitle: 'Event Title',
    eventTitlePlaceholder: 'Give your event a catchy name',
    summary: 'Summary',
    summaryPlaceholder: 'Short description (10-200 characters)',
    description: 'Description',
    descriptionPlaceholder: 'Tell attendees about your event...',
    categories: 'Categories',
    coverImage: 'Cover Image',
    displayPicture: 'Display Picture',
    startDate: 'Start Date',
    endDate: 'End Date',
    timezone: 'Timezone',
    locationType: 'Location Type',
    venue: 'Venue',
    venuePlaceholder: 'Venue name or address',
    city: 'City',
    country: 'Country',
    onlineLink: 'Online Link',
    capacity: 'Capacity',
    onlineCapacity: 'Online Capacity',
    waitlist: 'Enable Waitlist',
    approvalRequired: 'Require Approval',
    publish: 'Publish',
    saveDraft: 'Save as Draft',
    next: 'Next',
    previous: 'Previous',
    addTicketTier: 'Add Ticket Tier',
    tierName: 'Tier Name',
    tierPrice: 'Price',
    tierQuantity: 'Quantity',
    steps: {
      basicInfo: 'Basic Information',
      basicInfoShort: 'Basic Info',
      media: 'Event Media',
      mediaShort: 'Media',
      schedule: 'Schedule',
      scheduleShort: 'Schedule',
      location: 'Location',
      locationShort: 'Location',
      pricing: 'Pricing',
      pricingShort: 'Pricing',
      tickets: 'Tickets & Registration',
      ticketsShort: 'Tickets',
      review: 'Review & Publish',
      reviewShort: 'Review',
    },
    requiredMark: '*',
    categoriesHint: 'Select 1-3 categories.',
    loadingCategories: 'Loading categories...',
    titleLengthHint: 'Min 5, Max 50 characters.',
    summaryLengthHint: 'Min 10, Max 200 characters.',
    descriptionLengthHint: 'Min 50 characters.',
    location: 'Location',
    eventFormat: 'Event Format',
    inPersonDesc: 'Host your event at a physical venue',
    onlineDesc: 'Host a virtual gathering',
    hybridDesc: 'Both in-person and online',
    locationSearch: 'Location Search',
    locationSearchHint: 'Search for a venue or address...',
    address: 'Address',
    notSet: 'Not set',
    programmeToggle: 'Event Schedule / Programme',
    programmeToggleDesc: 'Enable a multi-session programme for your event.',
    communityScheduleDesc: 'Let checked-in attendees create and host side events during your event.',
    onlineLinkPlaceholder: 'https://...',
    addressMapVenuePlaceholder: 'e.g. Tampi Bumi',
    addressMapDistrictPlaceholder: 'e.g. Pereng, Bumirejo, Lendah',
    addressMapCityPlaceholder: 'e.g. Jakarta',
    addressMapCountryPlaceholder: 'e.g. Indonesia',
    addressMapFullPlaceholder: 'Enter full address (e.g., 123 Main St, New York, NY 10001)',
    generateLinkTitle: 'Auto-generate a meeting link',
    generateLink: 'Generate Link',
    hybridFull: 'Hybrid (In Person + Online)',
    noneSelected: 'None selected',
    coverPreviewAlt: 'Cover preview',
    notUploaded: 'Not uploaded',
    avatarPreviewAlt: 'Avatar preview',
    format: 'Format',
    pricing: 'Pricing',
    eventType: 'Event Type',
    freeRsvp: 'Free RSVP',
    paidTicketed: 'Paid / Ticketed',
    ticketsAndRegistration: 'Tickets & Registration',
    tierDefaultName: 'Tier {{n}}',
    unlimited: 'Unlimited',
    spots: '{{count}} spots',
    totalCapacity: 'Total Capacity',
    visibility: 'Visibility',
    public: 'Public',
    private: 'Private',
    publishState: 'Publish State',
    prePublishChecklist: 'Pre-publish Checklist',
    checklistTitle: 'Event has a title',
    checklistSummary: 'Summary provided',
    checklistStartFuture: 'Start date is in the future',
    checklistLocation: 'Location details complete',
    checklistTierArt: 'All ticket tiers have artwork',
    datesLockedToday: 'Cannot change date/time for events starting today',
    eventPricing: 'Event Pricing',
    pricingQuestion: 'Is this a free or paid event?',
    freeEvent: 'Free Event',
    freeEventDesc: 'Attendees can RSVP for free',
    paidEvent: 'Paid Event',
    paidEventDesc: 'Sell tickets with custom prices',
    collectRegistrationInfo: 'Collect additional registration information',
    collectRegistrationInfoDesc: 'Ask guests extra questions when they register for your event.',
    registrationOptions: 'Registration options',
    personalInformation: 'Personal Information',
    name: 'Name',
    fromProfile: 'From profile',
    web3Identity: 'Web3 Identity',
    solanaAddress: 'Solana Address',
    customQuestions: 'Custom Questions',
    addQuestion: 'Add Question',
    noCustomQuestions: 'No custom questions',
    questionRequired: '• Required',
    enableToCollectFields: 'Enable the checkbox above to collect these fields when guests register.',
    phone: 'Phone',
    collectPhoneAria: 'Collect phone number',
    startDateTime: 'Start Date & Time',
    endDateTime: 'End Date & Time',
  },

  // ─── Manage Event ─────────────────────────────────────────────
  manage: {
    overview: 'Overview',
    attendees: 'Attendees',
    checkIn: 'Check-in',
    sessions: 'Sessions',
    community: 'Community',
    analytics: 'Analytics',
    settings: 'Settings',
    collaborators: 'Collaborators',
    totalAttendees: 'Total Attendees',
    checkedIn: 'Checked In',
    pending: 'Pending',
    waitlisted: 'Waitlisted',
    searchAttendees: 'Search attendees...',
    exportCsv: 'Export CSV',
    approve: 'Approve',
    reject: 'Reject',
    checkInManually: 'Check In',
    removeAttendee: 'Remove',
    inviteAttendee: 'Invite',
    addCollaborator: 'Add Collaborator',
    editEvent: 'Edit Event',
    cancelEvent: 'Cancel Event',
    deleteEvent: 'Delete Event',
  },

  // ─── Tickets ──────────────────────────────────────────────────
  tickets: {
    myTickets: 'My Tickets',
    ticket: 'Ticket',
    viewTicket: 'View Ticket',
    downloadTicket: 'Download',
    qrCode: 'QR Code',
    scanToCheckIn: 'Scan to Check In',
    ticketId: 'Ticket ID',
    purchasedAt: 'Purchased',
    redeemedAt: 'Redeemed',
    nftTicket: 'NFT Ticket',
    withdrawNft: 'Withdraw to Wallet',
    withdrawing: 'Withdrawing...',
    withdrawn: 'Withdrawn',
    viewOnExplorer: 'View on Explorer',
    generateQr: 'Generate QR',
    refreshQr: 'Refresh',
    tierArtAlt: '{{name}} ticket art',
    xOfYAvailable: '{{available}} of {{total}} available',
    saleStarts: 'Sale starts: {{date}}',
    saleEnds: 'Sale ends: {{date}}',
    saleEnded: 'Sale Ended',
    saleNotStarted: 'Sale Not Started',
    soldOut: 'Sold Out',
    buyTickets: 'Buy Tickets',
    backToEvent: 'Back to Event',
    scanEntryCode: 'Scan entry code',
    viewPoaNft: 'View PoA NFT ↗',
    cancelledMessage: 'This ticket has been cancelled.',
    inPersonalWallet: 'Ticket is in your personal wallet',
    custodyDescription: 'This ticket is held securely in the app. Withdraw it to your Solana wallet when you are ready.',
    withdrawToWallet: 'Withdraw to {{address}}',
    withdrawalSuccess: 'Withdrawal initiated successfully!',
    withdrawalFailed: 'Withdrawal failed. Please try again.',
    proofOfAttendance: 'Proof of attendance',
    defaultEventTitle: 'Community event',
    noTicketsFound: 'No tickets found',
    noUpcoming: 'No upcoming tickets',
    noPast: 'No past tickets',
    filteredCount: '{{filtered}} of {{total}} tickets',
    viewAllCount: 'View all {{count}} tickets',
    tierSaved: 'Ticket tier saved',
    failedToSaveTier: 'Failed to save tier',
    invalidCheckInCode: 'Invalid or expired check-in code',
    attendeeCheckedIn: 'Attendee checked in',
    poaTransferred: 'POA transferred to your wallet',
    tierNameRequired: 'Tier name is required',
    connectWalletFirst: 'Please connect your wallet first',
    signInToPurchase: 'Please sign in to purchase tickets',
    failedToPurchaseTickets: 'Failed to purchase tickets',
    tierNamePlaceholder: 'e.g., General Admission, VIP, Early Bird',
    tierDescriptionPlaceholder: 'Brief description of what this tier includes',
    unlimitedPlaceholder: 'Unlimited',
    pricePlaceholder: '0.00',
  },

  // ─── Profile (merged with existing keys) ──────────────────────
  profile: {
    editTitle: 'Edit Profile',
    save: 'Save',
    saving: 'Saving...',
    cancel: 'Cancel',
    loading: 'Loading...',
    username: 'Username',
    usernameHint: 'Must be between 3 and 30 characters. Letters, numbers, hyphens, and underscores only.',
    usernameAvailable: 'Username is available',
    usernameTaken: 'Username is already taken',
    email: 'Email',
    displayName: 'Display name',
    location: 'Location',
    bio: 'Bio',
    bioPlaceholder: 'Tell us about yourself...',
    usernamePlaceholder: 'username',
    twitterHandlePlaceholder: 'handle',
    website: 'Website',
    twitter: 'X / Twitter',
    github: 'GitHub',
    organizedEvents: 'Organized Events',
    stats: {
      organized: 'Events organized',
      attending: 'Events attending',
      tickets: 'Tickets',
    },
    walletAddress: 'Wallet',
    unlinkXFailed: 'Failed to unlink X account',
    redirecting: 'Redirecting to sign in...',
    bannerAlt: "{{username}}'s banner",
    unlinkXTitle: 'Unlink X Account',
    unlinkXDescription: "Are you sure you want to unlink your X account? You won't be able to display your X verification status anymore.",
    tabs: {
      events: 'My Events',
      tickets: 'Tickets',
      notifications: 'Notifications',
      settings: 'Settings',
    },
    profileMedia: 'Profile Media',
    bannerImage: 'Banner Image',
    avatarImage: 'Avatar Image',
    mediaHint: "High-quality images help your profile stand out. We'll automatically optimize them for fast loading.",
    notificationPreferences: 'Notification preferences',
    notificationPreferencesHint: 'Choose which email notifications you receive.',
    manageEmailNotifications: 'Manage email notifications',
    connectedAccounts: 'Connected Accounts',
    deleteAccount: 'Delete account',
    deleteAccountTitle: 'Delete your account',
    deleteAccountDescription: 'This action is permanent. Your profile, events, RSVPs, tickets, and notifications will be removed.',
    deleteAccountConfirmLabel: 'Type your username to confirm',
    deleteAccountConfirmButton: 'Delete my account',
    deleteAccountCancel: 'Cancel',
    notFound: 'Profile not found',
    notFoundTitle: 'Profile not found',
    notFoundDescription: 'This profile may not exist or the username may be incorrect.',
    privateTitle: 'This profile is private',
    privateDescription: 'The owner has restricted who can view this profile.',
    editProfile: 'Edit profile',
    editShort: 'Edit',
    avatarAlt: "{{username}}'s avatar",
    xVerified: 'X verified',
    xLinked: 'X linked',
    viewOnX: 'View on X',
    rsvpCount: '{{count}} attending',
    rsvpConfirmed: 'Confirmed',
    joined: 'Joined',
    attendingEvents: 'Attending Events',
    searchPlaceholder: 'Search by username or name...',
    searchNoResults: 'No users found',
    searchMinChars: 'Type at least 2 characters to search',
    searchResultUser: 'User',
    searchInitial: 'U',
    language: 'Language',
    dangerZone: 'Danger Zone',
    uploadAvatar: 'Upload Photo',
    eventsOrganized: 'Events Organized',
    eventsAttended: 'Events Attended',
    dataExport: {
      title: 'Export your data',
      description: 'Download a machine-readable JSON copy of all personal data we store about you. Processing usually takes 5–10 minutes.',
      request: 'Export my data',
      requesting: 'Requesting export…',
      preparing: 'Preparing your export…',
      requestedAt: 'Requested {{date}}',
      readyHint: 'Your export is ready. Download before {{date}}.',
      download: 'Download my data',
      failed: 'Your export failed. Please try again.',
      tryAgain: 'Try again',
      expired: 'Your previous export has expired.',
      requestNew: 'Request new export',
      beforeDelete: 'Consider exporting your data before deleting your account.',
      beforeDeleteLink: 'Go to data export',
    },
    providers: {
      google: 'Google Account',
      apple: 'Apple Account',
      x: 'X Account',
      wallet: 'Wallet',
      linked: 'Linked',
      notLinked: 'Not linked',
      unlink: 'Unlink',
      unlinkXTitle: 'Unlink X Account',
      unlinkXDescription: 'Are you sure you want to unlink your X account?',
      unlinkGoogleTitle: 'Unlink Google Account',
      unlinkGoogleDescription: 'Are you sure you want to unlink your Google account?',
    },
    errors: {
      updateFailed: 'Failed to update profile',
      deleteFailed: 'Failed to delete account',
      searchFailed: 'Failed to search profiles',
      unlinkFailed: 'Failed to unlink account',
    },
  },

  // ─── Settings ─────────────────────────────────────────────────
  settings: {
    connectedAccounts: {
      title: 'Connected Accounts',
      description: 'Manage sign-in methods linked to your Creatix account.',
      linkProvider: 'Link',
      unlinkProvider: 'Unlink',
      unlinkConfirm: 'Unlink this account?',
      unlinkWarning:
        'Are you sure you want to unlink {{provider}}? You will need another sign-in method to access your account.',
      unlinkWarningGeneric:
        'Are you sure? You will need another sign-in method to access your account.',
      providerLinked: '{{provider}} linked successfully',
      providerAlreadyLinked:
        'This account is already linked to another Creatix profile. Sign in with that provider instead.',
      cannotUnlinkLast: 'Cannot unlink your last authentication method',
      linked: 'Linked',
      notLinked: 'Not linked',
      onlyMethod: 'Only sign-in method',
      unlinked: 'Account unlinked',
      linkFailed: 'Failed to link account',
      loadFailed: 'Failed to load connected accounts',
      linkWalletTitle: 'Link Solana wallet',
      linkWalletDescription: 'Sign a message with your connected wallet to verify ownership.',
      linkWalletConnect: 'Connect a Solana wallet extension to continue.',
      providers: {
        google: 'Google',
        apple: 'Apple',
        x: 'X',
        wallet: 'Solana Wallet',
      },
    },
  },

  // ─── Notifications ────────────────────────────────────────────
  notifications: {
    title: 'Notifications',
    markAllRead: 'Mark all as read',
    noNotifications: 'No notifications yet',
    rsvpConfirmed: 'Your RSVP is confirmed',
    rsvpWaitlisted: "You're on the waitlist",
    rsvpPromoted: "You've been confirmed from the waitlist",
    eventReminder: 'Event starting soon',
    checkedIn: 'Successfully checked in',
    nftMinted: 'Your POA NFT has been minted',
    emailPreferences: 'Email Preferences',
    pushEnabled: 'Push notifications enabled',
    enablePush: 'Enable Push Notifications',
    delete: 'Delete notification',
    empty: 'No notifications yet',
    invitationTitle: 'Invitation: {{eventTitle}}',
    invitedYou: '{{inviterName}} invited you',
    criticalUpdates: 'Critical updates',
    criticalUpdatesDesc: 'Event cancelled, rescheduled, or major changes; invitations; collaborator added/removed',
    myActivity: 'My activity',
    myActivityDesc: 'RSVP confirmation, approval, rejection; ticket ready; check-in confirmation',
    reminders: 'Reminders',
    remindersDesc: '24h and 1h before event start',
    organizerActivity: 'Organizer activity',
    organizerActivityDesc: 'New RSVP, RSVP cancelled, invitation accepted (for events you organize)',
    sessionUpdates: 'Session updates',
    sessionUpdatesDesc: 'Session cancelled, rescheduled, or deleted',
    communityEvents: 'Community events',
    communityEventsDesc: 'New RSVP to your community event; RSVP cancelled',
    saved: 'Notification preferences saved',
    saveFailed: 'Failed to save',
    backToProfile: 'Back to profile',
    pageTitle: 'Notifications',
    pageDescription: 'Manage your notification center and email preferences.',
    notificationCenter: 'Notification center',
    browserPush: 'Browser push notifications',
    browserPushDesc: 'Get notified when you have new activity, even when the tab is closed.',
    pushDisabled: 'Push notifications disabled',
    pushDisabledError: 'Failed to disable',
    pushEnabledError: 'Failed to enable',
    disable: 'Disable',
    enable: 'Enable',
    enabled: 'Enabled',
    disabled: 'Disabled',
    emailNotifications: 'Email notifications',
    emailDesc: 'Choose which emails you receive. You can change these anytime.',
    deliveryChannel: 'Delivery channel',
    deliveryChannelDesc: 'Where to send notifications. In-app feed and browser push are always on.',
    channelEmail: 'Email',
    channelWhatsapp: 'WhatsApp',
    channelBoth: 'Both',
    whatsappNumber: 'WhatsApp number',
    whatsappNumberPlaceholder: '+62...',
    sendCode: 'Send code',
    sending: 'Sending...',
    codeSent: 'Code sent to your WhatsApp.',
    enterCode: 'Enter 6-digit code',
    verify: 'Verify',
    verifying: 'Verifying...',
    verified: 'Verified',
    removeNumber: 'Remove number',
    whatsappVerifyRequired: 'Verify your WhatsApp number first',
    saving: 'Saving...',
    savePreferences: 'Save preferences',
    cancel: 'Cancel',
    unsubscribeFooter: 'To stop all emails, use the "Unsubscribe from all" link in any Creatix email.',
  },

  // ─── Check-in ─────────────────────────────────────────────────
  checkIn: {
    title: 'Check-in',
    scanQr: 'Scan QR Code',
    enterCode: 'Enter Code',
    selfCheckIn: 'Self Check-in',
    checkInCode: 'Check-in Code',
    codePlaceholder: 'Enter 6-digit code',
    selfCheckInCodePlaceholder: '000 000',
    checkInSuccess: 'Checked in successfully!',
    alreadyCheckedIn: 'Already checked in',
    invalidCode: 'Invalid code',
    nftMinting: 'Minting your POA NFT...',
    nftMinted: 'POA NFT minted!',
    scanAnother: 'Scan Another',
    totalCheckedIn: '{{count}} checked in',
    failed: 'Failed to check in',
    signInTitle: 'Sign in to check in',
    signInDesc: 'You need to be signed in to check in at the venue.',
    signIn: 'Sign in',
    checkedIn: "You're checked in!",
    checkedInDesc: 'You can now create and attend community events during this event.',
    viewEvent: 'View event',
    checkInFailed: 'Check-in failed',
    checkedInShort: 'Checked in',
    checkInSuccessTitle: 'Check-in Successful',
    userCheckedIn: '{{username}} checked in!',
    ticketAlreadyUsed: 'Ticket already used!',
    ticketNotFoundForEvent: 'Ticket not found for this event',
    failedCheckInAttendee: 'Failed to check in attendee',
    failedProcessTicket: 'Failed to process ticket',
    failedSelfCheckIn: 'Failed to check in',
    failedLoadQr: 'Failed to load QR code',
    backToEvent: 'Back to event',
    checkingIn: 'Checking you in...',
    redirecting: 'Redirecting...',
    codeCopiedDesc: 'Check-in code copied to clipboard',
    errorGeneratingCode: 'Error generating code',
    failedToFetchCheckInCode: 'Failed to fetch check-in code',
    eventCheckInCode: 'Event Check-in Code',
    clickToCopy: 'Click to copy',
    refreshingIn: 'Refreshing in {{seconds}}s',
    selfCheckInCode: 'Self Check-in Code',
    checkInQrAria: 'Event Check-in QR Code',
    codeRefreshesIn: 'Code refreshes in {{seconds}} seconds',
    scanQrTitle: 'Scan to Check In',
    scanQrDesc: 'Point your camera at the QR code to instantly verify your attendance.',
  },

  // ─── RSVP ────────────────────────────────────────────────────
  rsvp: {
    youreIn: "You're in!",
    spotConfirmed: 'Your spot is confirmed',
    requestSent: 'Request sent',
    organizerReview: 'The organizer will review your registration answers',
    organizerReviewRequest: 'The organizer will review your request',
    addedToWaitlist: 'Added to waitlist',
    failedToAttend: 'Failed to attend',
    attendanceCancelled: 'Attendance cancelled',
    failedToCancel: 'Failed to cancel attendance',
    mustBeLoggedIn: 'You must be logged in to cancel attendance.',
    cancelRequestConfirm: 'Are you sure you want to cancel your attendance request?',
    leaveWaitlistConfirm: 'Are you sure you want to leave the waitlist? You will lose your position.',
    cancelAttendanceConfirm: 'Are you sure you want to cancel your attendance?',
    cancelRequestTitle: 'Cancel Request?',
    leaveWaitlistTitle: 'Leave Waitlist?',
    cancelAttendanceTitle: 'Cancel Attendance?',
    back: 'Back',
    canceling: 'Canceling...',
    confirm: 'Confirm',
    yourTicketQr: 'Your Ticket QR Code',
    ticketQrDesc: 'Present this QR code at the event entrance.',
    qrAutoRefresh: "QR code refreshes automatically when it's about to expire",
    close: 'Close',
    loading: 'Loading...',
    eventCancelled: 'Event Cancelled',
    checkedInAs: 'Checked in as {{otherTypeLabel}}',
    attendingAs: 'Attending {{otherTypeLabel}}',
    pendingApprovalAs: 'Pending approval for {{otherTypeLabel}}',
    onWaitlistAs: 'On waitlist for {{otherTypeLabel}}',
    switching: 'Switching...',
    switchTo: 'Switch to {{thisTypeLabel}}',
    checkIn: 'Check In',
    ticket: 'Ticket',
    pendingApproval: 'Pending Approval',
    waitlistPosition: 'You are #{{position}} on the waitlist',
    onWaitlist: 'You are on the waitlist',
    leaving: 'Leaving...',
    leaveWaitlist: 'Leave waitlist',
    eventEnded: 'Event Ended',
    attend: 'Attend',
    answerQuestions: 'Answer questions & request to attend',
    phonePlaceholder: '+1 234 567 8900',
    walletPlaceholder: '5eykt4...',
    requestToAttend: 'Request to Attend',
    joinWaitlist: 'Join Waitlist',
    joining: 'Joining...',
    attending: 'Attending...',
    chooseHowToAttend: 'Choose how to attend',
    hybridDesc: 'This is a hybrid event. Please select your attendance type to continue.',
    joinInPerson: 'Join In-person',
    inPersonDesc: 'Attend at the physical venue',
    joinOnline: 'Join Online',
    onlineDesc: 'Attend via the virtual link',
    cancel: 'Cancel',
    ticketGenerationSlow: 'Ticket generation is taking longer than expected.',
    retry: 'Retry',
    generatingTicket: 'Your ticket is being generated...',
    warning: 'Warning',
    waitlistWarning: 'The waitlist is active. Your spot will be immediately given to the next person on the waitlist. You likely won\'t be able to reclaim it.',
    capacityWarning: 'This event is at capacity. Someone else may take your spot immediately.',
    lostWaitlistSpot: 'You will lose your position.',
    clarityTitle: 'Choosing Your Attendance',
    clarityDesc: 'This hybrid event offers two ways to attend. Choose the option that works best for you.',
    inPersonAttendance: 'In-Person Attendance',
    attendAtVenue: 'Attend at {{venue}}',
    qrForVenueCheckIn: 'Receive a QR code for venue check-in',
    onlineAttendance: 'Online Attendance',
    joinViaVideo: 'Join via video link from anywhere',
    checkInAfterMeeting: 'After joining the meeting, come back to this event page to check in',
    checkInCodeInstructions: 'Enter the check-in code provided by the host during the online session',
    gotIt: 'Got it',
    failedToRsvp: 'Failed to RSVP',
    rsvpCancelledToast: 'RSVP cancelled',
    rsvpApproved: 'RSVP approved',
    rsvpRejected: 'RSVP rejected',
    failedToApproveRsvp: 'Failed to approve RSVP',
    failedToRejectRsvp: 'Failed to reject RSVP',
    attendanceTypeUpdated: 'Attendance type updated',
    switchedToInPerson: 'Switched to in-person',
    switchedToOnline: 'Switched to online',
    failedToSwitchAttendanceType: 'Failed to switch attendance type',
    attendeeRemoved: 'Attendee removed',
    failedToRemoveAttendee: 'Failed to remove attendee',
    failedToSaveRegistrationConfig: 'Failed to save registration config',
  },

  // ─── Community Events ─────────────────────────────────────────
  community: {
    title: 'Community Events',
    createEvent: 'Create Community Event',
    hostedBy: 'Hosted by',
    attendees: '{{count}} attending',
    rsvp: 'RSVP',
    noEvents: 'No community events yet.',
    noEventsSubtitle: 'Be the first to create a community event',

    // Community Schedule
    notEnabled: 'Community schedule not enabled',
    notEnabledDesc: 'This event does not have a community schedule.',
    backToEvent: 'Back to event',
    schedule: 'Community Schedule',
    scanQrInfo: 'Scan the check-in QR at the venue to create or RSVP to community events.',
    openCheckIn: 'Open check-in page',
    signInInfo: 'Sign in and check in at the venue to create or RSVP to community events.',
    signIn: 'Sign in',
    hostSideEvent: 'Host a side event',
    noEventsYet: 'No community events yet.',
    beFirst: 'Be the first to host one',

    // Community Event Card
    showMyQr: 'Show my QR',
    myCheckInQr: 'My check-in QR',
    showQrDesc: 'Show this to the host to get your NFT.',
    youreGoing: "You're going!",
    rsvpCancelled: 'RSVP cancelled',
    atCapacity: 'Event is at capacity',
    nft: 'NFT',
    claimed: 'Claimed',
    tbd: 'TBD',
    goingCapacity: '{{rsvpCount}}/{{capacity}} going',
    goingCount: '{{rsvpCount}} going',
    byUser: 'by @{{username}}',
    claimNft: 'Claim your NFT \u2192',
    edit: 'Edit',
    checkedIn: 'Checked in',
    cancel: 'Cancel',
    going: 'Going',
    failedToLoadQr: 'Failed to load QR',
    failedToUpdateRsvp: 'Failed to update RSVP',

    // Community Event Host Admin
    invalidQr: "Invalid QR \u2013 scan an attendee's check-in QR",
    alreadyClaimed: 'Already claimed',
    alreadyClaimedDesc: 'Attendee already has their NFT',
    checkedInNft: 'Checked in!',
    nftClaimed: 'NFT claimed successfully',
    manageCheckIn: 'Manage check-in',
    checkInOptions: 'Check-in options',
    checkInOptionsDesc: 'Choose how to check in attendees for their proof-of-attendance NFT.',
    qrForGuests: 'QR for guests to scan',
    getCheckInQr: 'Get check-in QR',
    checkInQr: 'Check-in QR',
    checkInQrDesc: 'Display this at your event. Attendees scan it to claim their NFT.',
    scanAttendeeQr: "Scan attendee's QR",
    openScanner: 'Open scanner',
    scannerDesc: 'Attendees show their "My check-in QR" \u2013 you scan it.',
    scannerTitle: 'Scan attendee QR',
    scannerTitleDesc: "Point camera at the attendee's check-in QR code.",
    checkInByUsername: 'Check in by username',
    searchUsername: 'Search @username...',
    typeToSearch: 'Type 2+ chars to search',
    noMatchingAttendees: 'No matching attendees',
    checkIn: 'Check in',
    failedToCheckIn: 'Failed to check in',
    coverImageAlt: 'Cover image for {{title}}',
    userAvatarAlt: '{{username}} avatar',
    proofOfAttendance: 'Proof of attendance',
    defaultEventTitle: 'Community event',
    eventUpdated: 'Community event updated',
    updateFailed: 'Failed to update community event',
    eventNotFound: 'Community event not found',
    backToSchedule: 'Back to community schedule',
    editEvent: 'Edit community event',
    editSubtitle: 'Update your side event details.',
    clickToChangeCover: 'Click to change cover',
    locationLabel: 'Location',
    venueAddress: 'Venue address',
    options: 'Options',
    claimNftFailed: 'Failed to claim NFT',
    signInToClaimNft: 'Sign in to claim your NFT',
    signInToClaimNftDesc: 'You need to be signed in to claim your proof of attendance NFT.',
    nftClaimedDesc: 'Your proof of attendance NFT is saved. View it in your profile under My Tickets.',
    viewInMyTickets: 'View in my tickets',
    couldNotClaimNft: "Couldn't claim NFT",
    claimingNft: 'Claiming your NFT...',
    viewSchedule: 'View community schedule',

    // Create / edit form placeholders
    titlePlaceholder: 'e.g. Dinner at the local spot',
    descriptionPlaceholder: 'Describe your event — what will happen, who should come?',
    venueLabelPlaceholder: 'e.g. Convention Center Lobby',
    venueDetailPlaceholder: 'e.g. Main lobby, Table 5',
    addressPlaceholder: 'e.g. 123 Main St, Room 204',
    unlimitedCapacityPlaceholder: 'Unlimited',
  },

  // ─── Sessions ─────────────────────────────────────────────────
  sessions: {
    title: 'Sessions',
    addSession: 'Add Session',
    speaker: 'Speaker',
    speakers: 'Speakers',
    duration: '{{duration}} min',
    room: 'Room',
    workshop: 'Workshop',
    talk: 'Talk',
    panel: 'Panel',
    break: 'Break',
    rsvpSession: 'Reserve Seat',
    cancelRsvp: 'Cancel Reservation',
    spotsLeft: '{{count}} spots left',
    full: 'Full',
    titlePlaceholder: 'Session title',
    customTypePlaceholder: 'e.g. Screening, Keynote, Round',
    locationPlaceholder: 'e.g. Main Stage, Hacker Den, Room B',
    speakerPlaceholder: 'Speaker name (optional)',
    descriptionPlaceholder: 'What attendees can expect (optional)',
    capacityPlaceholder: 'Leave blank for unlimited (open session)',
    selectOption: 'Select...',
  },

  // ─── Analytics ────────────────────────────────────────────────
  analytics: {
    title: 'Analytics',
    pageViews: 'Page Views',
    totalRsvps: 'Total RSVPs',
    confirmed: 'Confirmed',
    checkedIn: 'Checked In',
    checkInRate: 'Check-in Rate',
    waitlisted: 'Waitlisted',
    cancelled: 'Cancelled',
    rsvpTrend: 'RSVP Trend',
    deviceBreakdown: 'Device Breakdown',
    mobile: 'Mobile',
    desktop: 'Desktop',
    topLocations: 'Top Locations',
    exportData: 'Export Data',
  },

  // ─── Event Creation (additional keys) ──────────────────────────
  events: {
    createEvent: 'Create Event',
    createEventDesc: 'Create a new event on Creatix. Set up your event details, tickets, and registration.',
    createEventSubtitle: 'Create a free event and start building your community',
    generalAdmission: 'General Admission',
    draftRestored: 'Draft restored',
    draftRestoredDesc: 'Your previous progress has been loaded',
    ticketArtRequired: 'All ticket tiers must have ticket art uploaded',
    regQuestionsNotSaved: 'Registration questions could not be saved. You can configure them later in Manage Event.',
    restoreDraft: 'Restore previous draft?',
    restoreDraftDesc: 'We found auto-saved progress from your last session.',
    autoSaving: 'Auto-saving draft...',
    step: 'Step {{current}} of {{total}}',
    creating: 'Creating...',
    eventNotFound: 'Event not found',
    backToProfile: 'Back to Profile',
  },

  // ─── Manage Event ─────────────────────────────────────────────
  manageEvent: {
    title: 'Manage Event',
    overview: 'Overview',
    overviewShort: 'Home',
    checkIn: 'Check-in',
    checkInShort: 'Check',
    attendees: 'Attendees',
    registration: 'Registration',
    registrationShort: 'Reg',
    insights: 'Insights',
    programme: 'Programme',
    programmeShort: 'Prog',
    collaborators: 'Collaborators',
    collaboratorsShort: 'Collab',
    invitations: 'Invitations',
    invitationsShort: 'Invite',
    promoImages: 'Promo Images',
    promoImagesShort: 'Images',
    settings: 'Settings',
    settingsShort: 'Set',
    checkInUnavailable: 'Check-in temporarily unavailable',
    loadingProgramme: 'Loading programme...',

    // ─── SettingsForm ─────────────────────────────────────────
    settingsUpdated: 'Event settings updated successfully!',
    updateFailed: 'Update Failed',
    registrationQuestionnaire: 'Registration questionnaire',
    regQuestionnaireDesc: 'Configure what guests answer when they RSVP. Click Save changes when done.',
    ticketArtRequired: 'Please upload ticket art for all ticket tiers before saving',
    cannotChangeFormat: 'Cannot Change Format',
    formatChangeWarning: 'This change would affect existing attendees',
    saveChanges: 'Save changes',
    saving: 'Saving...',
    savingChanges: 'Saving changes...',
    changeFormat: 'Change Event Format?',
    keepCurrentFormat: 'Keep Current Format',
    yesChangeFormat: 'Yes, Change Format',
    changingFormat: 'Changing format...',
    eventCancelled: 'Event cancelled successfully',
    cancellationFailed: 'Cancellation Failed',
    eventSettings: 'Event settings',
    eventSettingsDesc: 'Update your event the same way you created it — details, tickets, approval, and registration questions.',
    fixErrorsBeforeSaving: 'Please fix the following before saving:',
    couldNotSave: 'Could not save',
    questionnaireNotSaved: 'Other settings were saved, but the registration questionnaire was not saved.',
    eventSaved: 'Event saved',
    formatWillCancel: 'Switching from {{oldType}} to {{newType}} will cancel {{count}} affected ({{affectedType}}) attendees. They will be notified.',

    // ─── EventDangerZone ──────────────────────────────────────
    dangerZone: 'Danger Zone',
    cancelEvent: 'Cancel Event',
    cancelEventDesc: 'This will cancel the event and prevent any further registrations or check-ins. This action cannot be undone.',
    cannotCancelToday: 'Events starting today cannot be cancelled',
    areYouSure: 'Are you absolutely sure?',
    cancelWarning: 'This action will mark the event as <strong>Cancelled</strong>.',
    ticketSalesStopped: 'Ticket sales and RSVPs will be stopped immediately.',
    eventPageBadge: 'The event page will show a "Cancelled" badge.',
    keepEvent: 'Keep Event',
    cancelling: 'Cancelling...',
    yesCancelEvent: 'Yes, Cancel Event',
    deleteEvent: 'Delete Event Permanently',
    deleteEventDesc: 'This will permanently delete this draft event and all associated data. This action cannot be undone.',
    deleteWarning: 'The following will be deleted:',
    deletePermanent: 'Yes, Delete Permanently',
    cannotUndo: 'This action cannot be undone.',
    deleting: 'Deleting...',
    yesDelete: 'Yes, Delete Permanently',
    deleteConfirmTitle: 'Delete this event permanently?',
    deleteConfirmDesc: 'This will permanently remove the event <strong>{{title}}</strong> and all of its data.',
    deletedTypes: {
      ticketTiers: 'Ticket tiers and all sold tickets',
      rsvpRecords: 'RSVP and check-in records',
      promoImages: 'Promotional images',
      programmeSessions: 'Programme / schedule sessions',
      collaboratorAssignments: 'Collaborator assignments and invitations',
      registrationResponses: 'Registration questionnaire responses',
    },

    // ─── PromotionalImageManager ──────────────────────────────
    imagesUploaded: '{{count}}/{{max}} images uploaded',
    upload: 'Upload',
    uploading: 'Uploading...',
    noPromoImages: 'No promotional images yet',
    uploadToShowcase: 'Upload images to showcase your event',
    deletePromoImage: 'Delete Promotional Image',
    deletePromoImageDesc: 'Are you sure you want to delete this image? This action cannot be undone.',
    moveEarlier: 'Move earlier',
    moveLater: 'Move later',
    deleteImage: 'Delete image',
    viewRegistrationResponses: 'View registration responses',

    // ─── AddQuestionModal ─────────────────────────────────────
    questionTypeText: 'Text',
    questionTypeOptions: 'Options',
    questionTypeSocial: 'Social Profile',
    questionTypeCompany: 'Company',
    questionTypeCheckbox: 'Checkbox',
    questionTypeTerms: 'Terms',
    questionTypePhone: 'Phone',
    questionTypeWebsite: 'Website',
    editQuestion: 'Edit Question',
    addQuestion: 'Add Question',
    addQuestionDesc: 'Ask guests custom questions when they register.',
    backToTypes: '← Back to question types',
    questionLabel: 'Question label',
    questionLabelPlaceholder: 'e.g. Dietary requirements',
    placeholderOptional: 'Placeholder (optional)',
    placeholderExample: 'e.g. Vegetarian, allergies...',
    allowMultiline: 'Allow multiline (paragraph)',
    optionsOnePerLine: 'Options (one per line)',
    optionN: 'Option {{n}}',
    addOption: '+ Add option',
    termsText: 'Terms text',
    termsPlaceholder: 'I agree to the terms and conditions...',
    required: 'Required',

    // ─── AttendeeResponsesModal ───────────────────────────────
    registrationResponses: 'Registration Responses',
    name: 'Name',
    email: 'Email',
    phone: 'Phone',
    solanaWallet: 'Solana Wallet',
    noResponses: 'No additional responses recorded.',

    // ─── RegistrationSettings ─────────────────────────────────
    registrationQuestions: 'Registration Questions',
    regQuestionsDesc: 'We will ask guests the following questions when they register for the event.',
    personalInfo: 'Personal Information',
    nameFormat: 'Name format',
    nameFormatDesc: 'How guest names are collected at registration',
    fullName: 'Full name',
    firstLastName: 'First and last name',
    emailLabel: 'Email',
    fromProfile: 'From profile',
    web3Identity: 'Web3 Identity',
    solanaAddress: 'Solana Address',
    collectWalletDesc: 'Collect wallet address',
    customQuestions: 'Custom Questions',
    noCustomQuestions: 'No custom questions. Click "Add Question" to add one.',

    // ─── EnableAnalyticsCard ──────────────────────────────────
    enableAnalytics: 'Enable Analytics',
    enableAnalyticsDesc: 'Track page views, registrations, check-ins, and audience insights from this point forward. Once enabled, analytics cannot be turned off.',
    enabling: 'Enabling…',
    enableAnalyticsButton: 'Enable Analytics',
    enableAnalyticsFailed: 'Failed to enable analytics. Please try again.',

    // ─── OverviewTab ──────────────────────────────────────────
    viewPublicPage: 'View Public Page',
    rsvps: 'RSVPs',
    inPerson: 'In-Person',
    online: 'Online',
    ticketsSold: 'Tickets Sold',
    eventDate: 'Event Date',
    quickActions: 'Quick Actions',
    editSettings: 'Edit Settings',
    viewAttendees: 'View Attendees',

    // ─── AttendeesTab ─────────────────────────────────────────
    attendeeManagement: 'Attendee Management',
    searchAttendees: 'Search attendees by name or email...',
    typeToSearch: 'Type at least 3 characters to search',
    exporting: 'Exporting…',
    exportCsv: 'Export CSV',
    allAttendees: 'All Attendees',
    inPersonOnly: 'In-person Only',
    onlineOnly: 'Online Only',
    pending: 'Pending',
    confirmed: 'Confirmed',
    checkedIn: 'Checked In',
    waitlist: 'Waitlist',
    noPending: 'No pending approval requests',
    noConfirmed: 'No confirmed attendees yet',
    noCheckedIn: 'No one has checked in yet',
    waitlistEmpty: 'Waitlist is empty',
    approve: 'Approve',
    reject: 'Reject',
    onWaitlist: 'On Waitlist',
    checked: 'Checked',
    toOnline: 'To Online',
    toInPerson: 'To In-Person',
    remove: 'Remove',
    responses: 'Responses',
    loadMore: 'Load more attendees',
    switchType: 'Switch Attendance Type',
    switchDesc: 'Switch <strong>{{name}}</strong> to <strong>{{type}}</strong>? ',
    switchDescOnline: ' They will lose access to in-person check-in and receive an online attendance link instead.',
    switchDescInPerson: ' They will lose online access and need to check in at the venue instead.',
    removeAttendee: 'Remove Attendee',
    removeAttendeeDesc: 'Are you sure you want to remove <strong>{{name}}</strong> from this event? This will cancel their RSVP and free up their spot for waitlisted attendees.',

    // ─── CheckInTab ───────────────────────────────────────────
    eventCheckIn: 'Event Check-in',
    eventCheckInDescDefault: 'Scan attendee QR tickets manually or search for attendees below.',
    eventCheckInDescOnlineHybrid: 'Use the dynamic code for self check-in, or search for attendees below to check them in manually.',
    failedToLoadStats: 'Failed to load attendee stats',
    totalCheckedIn: 'Total Checked In',
    physical: 'Physical',
    virtual: 'Virtual',
    showCheckInCode: 'Show Check-In Code',
    close: 'Close',
    openManualScanner: 'Open Manual Scanner',
    orSearchAttendees: 'Or search attendees',
    recentCheckIns: 'Recent Check-ins',
    noCheckedInYet: 'No attendees checked in yet',
    getCheckInQrBanner: 'Get Check-in QR for Banner',
    bannerCheckInQr: 'Banner check-in QR',
    bannerCheckInQrDesc: 'Attendees scan this to check in on-site and unlock community events.',
    failedToLoadQr: 'Failed to load QR',

    // ─── CollaboratorsTab ─────────────────────────────────────
    collaboratorsSectionTitle: 'Event Collaborators',
    collaboratorsDesc: 'Collaborators have permission to manage attendees, check-in guests, and edit event details.',
    addNewCollaborator: 'Add New Collaborator',
    addNewCollabDesc: 'Grant management access to another user by email or username.',
    searchByUsername: 'Search by username or name',
    orEnterUsername: 'Enter username or email',
    enterManually: "Can't find them? Enter username or email",
    selectedCollaborator: 'Selected collaborator',
    clearSelection: 'Clear selection',
    alreadyCollaborator: 'This user is already a collaborator',
    selectRole: 'Select role',
    admin: 'Admin',
    noCollaborators: 'No collaborators yet',
    removeCollaboratorConfirm: 'Are you sure you want to remove this collaborator?',

    // ─── InvitationsTab ───────────────────────────────────────
    eventInvitations: 'Event Invitations',
    invitationsDesc: 'Invite people to your event by email. Invited guests will receive a notification and can RSVP directly.',
    inviteByUsername: 'Invite by Username',
    inviteByUsernameDesc: 'Search for a registered user by username or email.',
    sendInvitation: 'Send Invitation',
    sendInvitationDesc: 'Enter an email address to send an event invitation.',
    enterEmail: 'Enter email address',
    sendInvite: 'Send Invite',
    noInvitations: 'No invitations sent yet',
    inviteByEmail: 'Invite someone by email using the form above',
    cancelInvitationConfirm: 'Are you sure you want to cancel this invitation?',
    collaboratorAdded: 'Collaborator added',
    collaboratorRemoved: 'Collaborator removed',
    failedToAddCollaborator: 'Failed to add collaborator',
    failedToRemoveCollaborator: 'Failed to remove collaborator',
    invitationSent: 'Invitation sent',
    failedToSendInvitation: 'Failed to send invitation',
    invitationAccepted: 'Invitation accepted',
    failedToAcceptInvitation: 'Failed to accept invitation',
    invitationDeclined: 'Invitation declined',
    failedToDeclineInvitation: 'Failed to decline invitation',
    invitationCancelled: 'Invitation cancelled',
    failedToCancelInvitation: 'Failed to cancel invitation',
    imageUploaded: 'Image uploaded',
    failedToUploadImage: 'Failed to upload image',
    imageDeleted: 'Image deleted',
    failedToDeleteImage: 'Failed to delete image',
    imagesReordered: 'Images reordered',
    failedToReorderImages: 'Failed to reorder images',
    exportFailed: 'Export failed',

    // ─── ProgrammeTab ─────────────────────────────────────────
    sessionDeleted: 'Session deleted',
    sessionPublished: 'Session published',
    sessionUnpublished: 'Session unpublished',
    deleteSessionConfirm: 'Are you sure you want to delete this session?',
    eventProgramme: 'Event Programme',
    programmeDesc: 'Manage the schedule and sessions for your event.',
    allDays: 'All Days',
    all: 'All',
    sessionsCount: '{{count}} session',
    sessionsCount_plural: '{{count}} sessions',
    sessionsCountDay: ' on this day',
    addSession: 'Add Session',
    noSessions: 'No sessions yet',
    noSessionsDesc: 'Start building your event programme.',
    addFirstSession: 'Add First Session',
    noSessionsDate: 'No sessions on this date',
    noSessionsDateDesc: 'Switch to another day or add a new session.',

    // ─── EventAnalytics ───────────────────────────────────────
    failedToLoadAnalytics: 'Failed to load analytics',
    noActivity: 'No activity yet',
    noActivityDesc: 'Analytics will appear here once attendees start registering and checking in to your event.',
    from: 'From',
    to: 'To',
    granularity: 'Granularity',
    hour: 'Hour',
    day: 'Day',
    week: 'Week',
    performanceSummary: 'Performance Summary',
    registrations: 'Registrations',
    checkIns: 'Check-ins',
    nftClaimed: 'NFT Claimed',
    attendance: 'Attendance',
    noShow: 'No-Show',
    peakArrival: 'Peak Arrival',
    pageViews: 'Page Views',
    totalRevenue: 'Total Revenue',
    registrationClaimBehavior: 'Registration & Claim Behavior',
    claimToCheckIn: 'Claim-to-Check-in',
    last24hSurge: 'Last 24h Surge',
    registrationsBeforeEvent: 'registrations before event',
    capacity: 'Capacity',
    unlimited: 'Unlimited',
    registrationTrend: 'Registration Trend',
    pageViewsOverTime: 'Page Views Over Time',
    attendanceType: 'Attendance Type',
    analyticsWaitlist: 'Waitlist',
    analyticsOnWaitlist: 'On Waitlist',
    analyticsConfirmed: 'Confirmed',
    conversionRate: 'Conversion Rate',
    sessions: 'Sessions',
    session: 'Session',
    analyticsRsvps: 'RSVPs',
    audienceDistribution: 'Audience Distribution',
    audienceEmpty: 'Detailed audience insights will appear for new registrations.',
    deviceType: 'Device Type',
    topCities: 'Top Cities',
    browser: 'Browser',
    ticketTiers: 'Ticket Tiers',
    registrationStatus: 'Registration Status',
    eventTimeline: 'Event Timeline',
    registrationGrowth: 'Registration Growth',
    checkInFlow: 'Check-in Flow',
    chartCumulative: 'Cumulative',
    chartDaily: 'Daily',
    chartViews: 'Views',
    chartSold: 'Sold',
    chartTotal: 'Total',
    peakPrefix: 'Peak:',
    analyticsCancelled: 'Cancelled',
  },

  // ─── Groups ───────────────────────────────────────────────────
  groups: {
    myGroups: 'My Groups',
    createTitle: 'Create Group',
    createButton: 'Create Group',
    name: 'Group name',
    namePlaceholder: 'e.g. Superteam Hub',
    description: 'Description',
    website: 'Website',
    websitePlaceholder: 'https://',
    industry: 'Industry',
    logo: 'Logo',
    banner: 'Banner',
    settings: 'Settings',
    settingsTitle: 'Group Settings',
    memberCount: '{{count}} members',
    upcomingEvents: 'Upcoming Events',
    pastEvents: 'Past Events',
    noUpcomingEvents: 'No upcoming events yet.',
    notFound: 'Group not found',
    empty: 'You are not a member of any groups yet.',
    emptyTitle: 'No groups yet',
    notFoundTitle: 'Group not found',
    loadFailed: 'Failed to load groups',
    created: 'Group created',
    createFailed: 'Failed to create group',
    updated: 'Group updated',
    updateFailed: 'Failed to update group',
    deleted: 'Group deleted',
    deleteFailed: 'Failed to delete group',
    deleteGroup: 'Delete group',
    deleteConfirmTitle: 'Delete this group?',
    deleteConfirmDesc: 'Events linked to this group will be unlinked but not deleted.',
    memberAdded: 'Member added',
    memberAddFailed: 'Failed to add member',
    memberRemoved: 'Member removed',
    memberRemoveFailed: 'Failed to remove member',
    removeMemberConfirmTitle: 'Remove this member?',
    removeMemberConfirmDesc: 'Remove {{name}} from the group? They will lose access to group settings.',
    addMember: 'Add member',
    inviteRole: 'Role',
    transferOwnership: 'Transfer ownership',
    transferOwnershipConfirm: 'Make this member the group owner? You will become an admin.',
    ownershipTransferred: 'Ownership transferred',
    ownershipTransferFailed: 'Failed to transfer ownership',
    membersReadOnly: 'Only owners and admins can manage group settings.',
    linkEvent: 'Link event',
    linkEventToGroupDesc: 'Link one of your events to this group.',
    noEventsToLink: 'No unlinked events to add.',
    memberUserIdPlaceholder: 'User ID (UUID)',
    linkOnCreate: 'Link to group (optional)',
    noGroup: 'No group',
    eventLinkTitle: 'Group',
    linkEventDesc: 'Link this event to one of your groups.',
    unlinkEvent: 'Unlink from group',
    noGroupsToLink: 'Join or create a group first.',
    noLinkedEvents: 'No events linked to this group.',
    tabs: {
      details: 'Details',
      members: 'Members',
      events: 'Events',
    },
    roles: {
      owner: 'Owner',
      admin: 'Admin',
      member: 'Member',
    },
    validation: {
      nameMin: 'Group name must be at least 2 characters',
      nameMax: 'Group name must be at most 100 characters',
      descriptionMax: 'Description must be at most 2000 characters',
      invalidUrl: 'Invalid URL',
    },
  },

  // ─── Dev/Test Login ───────────────────────────────────────────
  dev: {
    title: 'Dev/Test Login',
    username: 'Username',
    usernamePlaceholder: 'Enter username',
    password: 'Password',
    passwordPlaceholder: 'Dev password',
    loggingIn: 'Logging in...',
    login: 'Login (Dev Only)',
    loginFailed: 'Login failed',
    loginError: 'Failed to login',
  },

  // ─── Wallet ───────────────────────────────────────────────────
  wallet: {
    connected: 'Wallet connected!',
    readyToAttend: "You're ready to attend events",
    connect: 'Connect Wallet',
    connectedWallet: 'Connected Wallet',
    balance: 'Balance',
    balanceSol: '{{balance}} SOL',
    disconnect: 'Disconnect',
  },

  // ─── Common / Shared ──────────────────────────────────────────
  common: {
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    confirm: 'Confirm',
    back: 'Back',
    next: 'Next',
    previous: 'Previous',
    restore: 'Restore',
    discard: 'Discard',
    tryAgain: 'Try Again',
    continue: 'Continue',
    submit: 'Submit',
    close: 'Close',
    edit: 'Edit',
    update: 'Update',
    remove: 'Remove',
    add: 'Add',
    create: 'Create',
    publish: 'Publish',
    share: 'Share',
    copy: 'Copy',
    copied: 'Copied!',
    loading: 'Loading...',
    saving: 'Saving...',
    submitting: 'Submitting...',
    search: 'Search',
    filter: 'Filter',
    clearFilters: 'Clear Filters',
    viewAll: 'View all',
    seeMore: 'See more',
    seeLess: 'See less',
    optional: 'Optional',
    required: 'Required',
    or: 'or',
    and: 'and',
    yes: 'Yes',
    no: 'No',
    notAvailable: 'N/A',
    comingSoon: 'Coming Soon',
    new: 'New',
    free: 'Free',
    today: 'Today',
    guest: 'Guest',
    there: 'there',
    someone: 'Someone',
    yesterday: 'Yesterday',
    tomorrow: 'Tomorrow',
    dateAt: '{{date}} at {{time}}',
    keep: 'Keep',
    retry: 'Retry',
    switch: 'Switch',
    devnet: 'Devnet',
    connectedTo: 'Connected to {{network}}',
    notSet: 'Not set',
    enabled: 'Enabled',
    disabled: 'Disabled',
    download: 'Download',
    linkCopied: 'Link copied to clipboard!',
  },

  // ─── Validation / Error Messages ──────────────────────────────
  validation: {
    required: 'This field is required',
    titleMin: 'Title must be at least 5 characters',
    titleMax: 'Title must be at most 50 characters',
    summaryMin: 'Summary must be at least 10 characters',
    summaryMax: 'Summary must be at most 200 characters',
    invalidEmail: 'Invalid email address',
    invalidUrl: 'Invalid URL',
    capacityMin: 'Capacity must be at least 1',
    deadlineFuture: 'Deadline must be in the future',
    goalMin: 'Goal must be greater than 0',
    categoriesMin: 'Select at least 1 category',
    categoriesMax: 'Select at most 3 categories',
    categoriesRequired: 'At least 1 category is required',
    descriptionMin: 'Description must be at least 50 characters',
    cityCountryRequired: 'City and Country are required for in-person/hybrid events',
    onlineLinkRequired: 'Online link is required for online events',
    onlineLinkInvalid: 'Online link must be a valid URL starting with http:// or https://',
    startDateFuture: 'Start date must be in the future',
    endDateAfterStart: 'End date must be after start date',
    priceNonNegative: 'Price must be non-negative',
    integer: 'This value must be an integer',
    attendanceTypeMin: 'Select at least one attendance type',
    checkinCodeLength: 'Check-in code must be 8 characters',
    checkinCodeHex: 'Check-in code must be hexadecimal',
    provideIdentifier: 'Provide ticketId, rsvpId, or userId',
    usernameRegex: 'Username can only contain letters, numbers, hyphens, and underscores',
    empty: 'This field cannot be empty',
    userIdOrEmail: 'Either userId or email must be provided',
    optionsMin: 'Select and multiselect questions must have at least 2 options',
    imageType: 'File must be a JPEG, PNG, WebP, or GIF image',
    fileSizeMax: 'File size must be under 10 MB',
    imageMaxCount: 'Maximum 10 promotional images per event',
    invalidPhone: 'Invalid phone format',
    invalidWallet: 'Invalid Solana wallet',
  },

  // ─── Error States ─────────────────────────────────────────────
  errors: {
    generic: 'Something went wrong. Please try again.',
    notFound: 'Not found',
    unauthorized: 'You are not authorized to do this',
    networkError: 'Network error. Check your connection.',
    eventNotFound: 'Event not found',
    ticketNotFound: 'Ticket not found',
    sessionExpired: 'Your session has expired. Please sign in again.',
    capacityFull: 'This event is at full capacity',
    alreadyRsvp: 'You have already RSVPed to this event',
    appError: 'Application Error',
    appErrorDescription: 'A client-side error occurred. Please try refreshing the page.',
    stackTrace: 'Stack trace',
    refreshPage: 'Refresh Page',
    tryAgain: 'Try Again',
    locationLookupFailed: 'Location lookup failed',
    pageNotFound: 'Page Not Found',
    pageNotFoundDesc: "Sorry, we couldn't find the page you're looking for.",
    unauthorizedDesc: "You don't have permission to access this page.",
    serviceUnavailable: 'Service Unavailable',
    serviceUnavailableDesc: 'Our servers are experiencing issues. Please try again later.',
    genericTitle: 'Oops! Something went wrong',
    updateAvailable: 'Update Available',
    hardRefreshRequired: 'We just released a new version of Creatix! Please do a hard refresh (Ctrl+F5 or Cmd+Shift+R) to load it.',
    unknown: 'Unknown Error',
    unknownDesc: 'An unknown error has occurred.',
    errorCode: 'Error Code: {{status}}',
    loadTicketFailed: 'Failed to load ticket',
    uploadImageFailed: 'Failed to upload image',
    updateFailed: 'Update failed',
    exportScheduleFailed: 'Failed to export schedule',
  },

  // ─── Footer ───────────────────────────────────────────────────
  footer: {
    xAria: 'X (Twitter)',
    instagramAria: 'Instagram',
    emailAria: 'Email',
    brand: 'Creatix',
    tagline: 'Event Platform',
    upcomingEventCount_one: '{{count}} upcoming event',
    upcomingEventCount_other: '{{count}} upcoming events',
    privacyAria: 'Privacy Policy',
    termsAria: 'Terms of Service',
  },

  // ─── Legal ────────────────────────────────────────────────────
  legal: {
    privacyTitle: 'Privacy Policy',
    privacyDescription: 'Learn how Creatix collects, uses, and protects your personal information. Read our Privacy Policy.',
    termsTitle: 'Terms of Service',
    termsDescription: 'Read the Terms of Service for using Creatix. Learn about our policies, user agreements, and platform rules.',
    lastUpdated: 'Last updated: {{date}}',
    unsubscribed: "You're unsubscribed",
    unsubscribedDesc: "You won't receive event notification emails from Creatix anymore. You can still sign in and ",
    manageNotificationPreferences: 'manage your notification preferences',
    requiresLogin: '(requires login).',
    invalidLink: 'Invalid or expired link',
    missingToken: 'No unsubscribe token was provided.',
    invalidOrUsed: 'This unsubscribe link is invalid or has already been used.',
    backToCreatix: 'Back to Creatix',
    privacy: {
      section1Title: '1. Information We Collect',
      section1Intro: 'We collect information you provide directly to us when you create an account, create or attend events, or communicate with us. This includes:',
      section1Item1: 'Account information (username, email, wallet address)',
      section1Item2: 'Profile information (bio, avatar, social links)',
      section1Item3: 'Event information (when you create events)',
      section1Item4: 'Communication data (when you contact support)',
      section2Title: '2. How We Use Your Information',
      section2Intro: 'We use the information we collect to provide, maintain, and improve our services, including to:',
      section2Item1: 'Process transactions and send related information',
      section2Item2: 'Send you technical notices, updates, and support messages',
      section2Item3: 'Respond to your comments and questions',
      section2Item4: 'Monitor and analyze trends and usage',
      section3Title: '3. Data Sharing',
      section3Intro: 'We do not sell your personal data. We may share your information in the following circumstances:',
      section3Item1: 'With your consent',
      section3Item2: 'For legal reasons',
      section3Item3: 'With service providers who help us operate our business',
      section4Title: '4. Contact Us',
      section4Body: 'If you have any questions about this Privacy Policy, please contact us at creatixplatform@gmail.com.',
    },
    terms: {
      section1Title: '1. Acceptance of Terms',
      section1Body: 'By accessing or using our website and services, you agree to be bound by these Terms of Service and all applicable laws and regulations.',
      section2Title: '2. Use License',
      section2Body: "Permission is granted to temporarily download one copy of the materials (information or software) on Creatix's website for personal, non-commercial transitory viewing only.",
      section3Title: '3. Event Creation and Attendance',
      section3Intro: 'Users may create and attend events through our platform. By creating an event, you represent that:',
      section3Item1: 'You have the authority to host the event',
      section3Item2: 'The event does not violate any laws or regulations',
      section3Item3: 'The content of the event is not offensive or harmful',
      section4Title: '4. Blockchain Transactions',
      section4Body: 'Our platform utilizes blockchain technology. You acknowledge that blockchain transactions are irreversible and that you are solely responsible for ensuring the accuracy of your transaction details.',
      section5Title: '5. Limitation of Liability',
      section5Body: "In no event shall Creatix or its suppliers be liable for any damages (including, without limitation, damages for loss of data or profit, or due to business interruption) arising out of the use or inability to use the materials on Creatix's website.",
    },
  },

  // ─── Invitations ──────────────────────────────────────────────
  invitation: {
    invalid: 'Invalid Invitation',
    invalidDesc: 'This invitation link is invalid or has expired.',
    accepted: 'Invitation Accepted!',
    acceptedDesc: 'You have been added to the event. Check your events to see details.',
    viewMyEvents: 'View My Events',
    declined: 'Invitation Declined',
    declinedDesc: 'You have declined this event invitation.',
    promptTitle: "You're Invited!",
    promptDesc: 'You have been invited to an event. Would you like to accept or decline this invitation?',
    accept: 'Accept',
    decline: 'Decline',
  },

  // ─── Schedule (event programme page) ──────────────────────────
  schedule: {
    myProgramme: 'My Programme',
    listView: 'List View',
    gridView: 'Grid View',
    allRooms: 'All Rooms',
    allTypes: 'All Types',
    dayLabel: 'Day {{n}}',
    happeningNow: 'Happening Now',
    upNext: 'Up Next',
    startingSoon: 'Starting Soon',
    scheduleConflict: 'Schedule conflict: overlaps with {{count}} other session in your programme',
    scheduleConflict_other: 'Schedule conflict: overlaps with {{count}} other sessions in your programme',
    concurrent: 'Concurrent',
    rsvpToSeeLocation: 'RSVP to see location',
    unassigned: 'Unassigned',
    today: 'Today',
    addProgrammeToCalendar: 'Add Programme to Calendar',
    addFullScheduleToCalendar: 'Add Full Schedule to Calendar',
    subscribe: 'Subscribe',
    appleOutlook: 'Apple / Outlook',
    autoUpdatesNative: 'Auto-updates native calendar',
    googleCalendar: 'Google Calendar',
    addViaBrowser: 'Add via web browser',
    download: 'Download',
    downloadIcs: 'Download .ics File',
    staticFileNoUpdates: 'Static file, no auto-updates',
  },

  // ─── Partner Portal ───────────────────────────────────────────
  partner: {
    pages: {
      directory: 'Partner Directory',
      directoryDesc: 'Discover Creatix partners and collaborators.',
      register: 'Become a Partner',
      registerDesc: 'Join the Creatix partner network.',
      detail: 'Partner Profile',
      detailDesc: 'View partner profile and metrics.',
      dashboard: 'Partner Dashboard',
      dashboardDesc: 'Manage your partner profile and track progress.',
    },
    fields: {
      type: 'Partner type',
      organizationName: 'Organization name',
      organizationNamePlaceholder: 'Your organization or brand',
      description: 'Description',
      descriptionPlaceholder: 'Tell us about your organization',
      website: 'Website',
      capabilities: 'Capabilities',
      social: {
        twitter: 'Twitter / X',
        instagram: 'Instagram',
        linkedin: 'LinkedIn',
        youtube: 'YouTube',
      },
    },
    types: {
      community: 'Community',
      eo: 'Event Organizer',
      media: 'Media',
      production: 'Production',
      operations: 'Operations',
      kol: 'KOL / Influencer',
      web3_project: 'Web3 Project',
      vendor: 'Vendor',
    },
    tiers: {
      Bronze: 'Bronze',
      Silver: 'Silver',
      Gold: 'Gold',
    },
    status: {
      Pending: 'Pending',
      Active: 'Active',
      Suspended: 'Suspended',
      Deactivated: 'Deactivated',
    },
    capabilities: {
      co_hosting: 'Co-hosting',
      event_promotion: 'Event promotion',
      media_coverage: 'Media coverage',
      content_creation: 'Content creation',
      av_production: 'AV production',
      lighting: 'Lighting',
      decor: 'Decor',
      venue_provision: 'Venue provision',
      catering: 'Catering',
      security: 'Security',
      staffing: 'Staffing',
      ticket_sales: 'Ticket sales',
      sponsorship: 'Sponsorship',
      brand_activation: 'Brand activation',
      web3_integration: 'Web3 integration',
      community_building: 'Community building',
      influencer_promotion: 'Influencer promotion',
    },
    validation: {
      typeRequired: 'Partner type is required',
      nameMinLength: 'Organization name must be at least 2 characters',
      nameMaxLength: 'Organization name must be 200 characters or less',
      descriptionMaxLength: 'Description must be 1000 characters or less',
      invalidUrl: 'Invalid URL',
    },
    registration: {
      step: 'Step {{current}} of {{total}}',
      selectType: 'What type of partner are you?',
      organizationDetails: 'Organization details',
      selectCapabilities: 'Select your capabilities',
      capabilitiesHint: 'Choose the services you can provide.',
      linksAndSocial: 'Links & social',
      review: 'Review your application',
      back: 'Back',
      next: 'Next',
      submit: 'Submit application',
      successTitle: 'Application submitted',
      successDesc: 'We will review your application and get back to you soon.',
      duplicateName: 'This organization name is already registered.',
      failed: 'Failed to submit application. Please try again.',
      anonymousNote:
        'If you want to manage your partner profile later, create an account and we’ll help link it.',
    },
    directory: {
      back: 'Back to directory',
      searchPlaceholder: 'Search partners...',
      filterType: 'Type',
      filterTier: 'Tier',
      allTypes: 'All types',
      allTiers: 'All tiers',
      apply: 'Apply',
      clear: 'Clear',
      found: '{{count}} partners found',
      empty: 'No partners found.',
      emptyTitle: 'No partners yet',
      emptyDesc: 'Try adjusting your filters or check back later.',
      loadFailed: 'Failed to load partners.',
      retry: 'Retry',
      loadMore: 'Load more',
      becomePartner: 'Become a partner',
      eventsCoHosted: '{{count}} events co-hosted',
    },
    profile: {
      notFound: 'Partner not found.',
      notFoundTitle: 'Partner not found',
      loadFailed: 'Failed to load partner profile.',
      loadFailedTitle: 'Could not load partner',
      memberCount: '{{count}} members',
      metrics: 'Performance metrics',
    },
    metrics: {
      eventsCoHosted: 'Events co-hosted',
      attendeesReferred: 'Attendees referred',
      eventsPromoted: 'Events promoted',
      rating: 'Rating',
    },
    dashboard: {
      noPartner: 'You do not have a partner profile yet.',
      registerCta: 'Register as a partner',
      pendingTitle: 'Application under review',
      pendingDesc: 'Your partner application is pending approval. We will notify you once it is reviewed.',
      noMetrics: 'No metrics available yet.',
      comingSoon: 'Coming soon.',
      tabs: {
        overview: 'Overview',
        tier: 'Tier progress',
        settings: 'Settings',
        coHosted: 'Co-hosted events',
        referrals: 'Referral stats',
      },
    },
    tierProgress: {
      title: 'Progress toward {{tier}}',
      summary: '{{met}} of {{total}} criteria met ({{required}} required)',
      maxTier: 'You have reached the maximum tier.',
      eventsCoHosted: 'Co-hosted events',
      attendeesReferred: 'Referred attendees',
      monthsActive: 'Months active',
      rating: 'Rating',
      eventsPromoted: 'Events promoted',
      nextEvaluation: 'Next tier evaluation: {{date}}',
    },
    settings: {
      logo: 'Logo',
      uploadLogo: 'Upload logo',
      logoUpdated: 'Logo updated',
      logoFailed: 'Failed to upload logo',
      socialLinks: 'Social links',
      save: 'Save changes',
      saved: 'Profile updated',
      saveFailed: 'Failed to save changes',
    },
  },

  // ─── PWA / Mobile ─────────────────────────────────────────────
  pwa: {
    offlineMessage: 'You are offline. Last updated: {{time}}',
    backOnline: 'Back online',
    updateAvailable: 'Update available — tap to refresh',
    updateNow: 'Update',
    installTitle: 'Install Creatix',
    installPrompt: 'Install Creatix for quick access',
    install: 'Install',
    iosInstructions: 'Tap Share, then Add to Home Screen',
    mobileNav: 'Mobile navigation',
    queuedOffline: 'Saved offline — will sync when connected',
    qrOfflineWarning: 'Using cached QR code while offline. Connect to refresh.',
    qrExpiryWarning: 'QR code may be expired. Connect to the internet to refresh.',
    dismissBanner: 'Dismiss',
    scannerTorch: 'Toggle torch',
    scannerFullscreen: 'Full screen',
    scannerExitFullscreen: 'Exit full screen',
  },

  report: {
    title: 'Report content',
    reportEvent: 'Report event',
    description: 'Tell us what is wrong. Our team will review your report.',
    reasonPlaceholder: 'Describe the issue…',
    reasonRequired: 'Please provide a reason for your report.',
    submit: 'Submit report',
    submitting: 'Submitting…',
    submitFailed: 'Could not submit report. Please try again.',
  },

  // ─── Admin Dashboard ──────────────────────────────────────────
  admin: {
    title: 'Admin Dashboard',
    accessDenied: 'You do not have admin access',
    overview: 'Overview',
    users: 'Users',
    events: 'Events',
    partners: 'Partners',
    reports: 'Reports',
    auditLog: 'Audit Log',
    totalUsers: 'Total Users',
    totalEvents: 'Total Events',
    publishedEvents: 'Published Events',
    totalRsvps: 'Total RSVPs',
    totalTickets: 'Total Tickets',
    totalCheckIns: 'Check-ins',
    totalNfts: 'NFTs Minted',
    activePartners: 'Active Partners',
    searchUsers: 'Search users…',
    searchEvents: 'Search events…',
    role: 'Role',
    provider: 'Provider',
    status: 'Status',
    suspend: 'Suspend',
    unsuspend: 'Unsuspend',
    changeRole: 'Change Role',
    changeState: 'Change State',
    resolve: 'Resolve',
    dismiss: 'Dismiss',
    loading: 'Loading…',
    noResults: 'No results',
    auditNotAvailable: 'Audit logging is not yet available',
    audit: {
      unavailable: 'Audit log is temporarily unavailable. Please check back later.',
      action: 'Action',
      category: 'Category',
    },
    auditCategory: 'Category',
    auditAllCategories: 'All categories',
    auditActionFilter: 'Filter by action…',
    reportReason: 'Reason',
    reportDetail: 'Report details',
    resolveNotePlaceholder: 'Optional note for resolution…',
    createdAt: 'Created',
    organizer: 'Organizer',
    eventState: 'State',
    usersByProvider: 'Users by provider',
    eventsByState: 'Events by state',
    monthlyGrowth: 'Monthly growth (12 months)',
    month: 'Month',
    newUsers: 'New users',
    newEvents: 'New events',
    userDetail: 'User details',
    eventDetail: 'Event details',
    noRole: 'No admin role',
    suspendReason: 'Reason',
    suspendReasonPlaceholder: 'Reason for suspension…',
    suspended: 'Suspended',
    active: 'Active',
    eventsCreated: 'Events created',
    rsvps: 'RSVPs',
    joined: 'Joined',
    tickets: 'Tickets',
    checkIns: 'Check-ins',
    sessions: 'Sessions',
    viewPublicPage: 'View public page',
    forceDelete: 'Force delete',
    forceDeleteConfirm: 'Permanently delete this event? This cannot be undone.',
    stateReasonPlaceholder: 'Optional reason for state change…',
    cancelEventWarning: 'Cancelling a published event will notify all confirmed attendees.',
    allRoles: 'All roles',
    allProviders: 'All providers',
    allStates: 'All states',
    actions: 'Actions',
    approve: 'Approve',
    email: 'Email',
    linkedProviders: 'Linked providers',
    description: 'Description',
    eventDate: 'Event date',
    dateFrom: 'From date',
    dateTo: 'To date',
    organizerIdFilter: 'Organizer user ID…',
    categoryFilter: 'Category…',
    contact: 'Contact',
    partnerType: 'Type',
    partnerTier: 'Tier',
    allStatuses: 'All statuses',
    suspendPartner: 'Suspend',
    deactivate: 'Deactivate',
    reactivate: 'Reactivate',
  },

  reviews: {
    sectionTitle: 'Reviews',
    writeReview: 'Write a Review',
    writeReviewDesc: 'Share your experience with other attendees.',
    yourRating: 'Your rating',
    reviewTextOptional: 'Review (optional)',
    postAnonymously: 'Post anonymously',
    submitReview: 'Submit Review',
    submitSuccess: 'Review submitted',
    submitError: 'Failed to submit review',
    ratingRequired: 'Please select a rating',
    ratingMin: 'Rating must be at least 1',
    ratingMax: 'Rating must be at most 5',
    textMax: 'Review must be under 500 characters',
    responseMax: 'Response must be under 1000 characters',
    rateStars: 'Rate {{count}} stars',
    anonymousAttendee: 'Anonymous Attendee',
    organizerResponse: 'Organizer response',
    edit: 'Edit',
    delete: 'Delete',
    respond: 'Respond',
    reviewCount: '{{count}} reviews',
    noReviewsYet: 'No reviews yet',
    emptyState: 'Be the first to share your experience.',
    sortNewest: 'Newest',
    sortHighest: 'Highest rating',
    sortLowest: 'Lowest rating',
    loadMore: 'Load more',
    newOrganizer: 'New organizer — no ratings yet',
    surveyTitle: 'Post-Event Survey',
    surveyDesc: 'Help the organizer improve future events.',
    surveyLoading: 'Loading survey…',
    surveyThankYou: 'Thank you!',
    surveyThankYouDesc: 'Your feedback has been submitted.',
    surveySubmit: 'Submit survey',
    surveySubmitSuccess: 'Survey submitted',
    surveySubmitError: 'Failed to submit survey',
    surveyRequiredError: 'Please answer: {{question}}',
    surveyConfigTitle: 'Post-Event Survey',
    surveyConfigDesc: 'Configure questions attendees answer after the event ends.',
    surveyConfigSaved: 'Survey configuration saved',
    surveyConfigError: 'Failed to save survey configuration',
    questionNumber: 'Question {{number}}',
    questionPlaceholder: 'Enter your question',
    requiredQuestion: 'Required',
    options: 'Options',
    optionPlaceholder: 'Option {{number}}',
    addOption: 'Add option',
    addQuestion: 'Add question',
    saveSurveyConfig: 'Save survey',
    questionRequired: 'Question is required',
    questionMax: 'Question must be under 500 characters',
    surveyMinQuestions: 'At least one question required',
    surveyMaxQuestions: 'Maximum 20 questions',
    questionType: {
      rating: 'Star rating',
      text: 'Text answer',
      single_choice: 'Single choice',
      multi_choice: 'Multiple choice',
    },
    rateSession: 'Rate',
    sessionRating: '{{rating}} avg',
  },

  streaming: {
    settingsTitle: 'Live streaming',
    settingsDesc: 'Configure how attendees watch your online stream.',
    typeLabel: 'Streaming type',
    typeExternal: 'External link',
    typeEmbedded: 'Embedded player',
    typeZoom: 'Zoom meeting',
    typeGoogleMeet: 'Google Meet',
    externalUrl: 'Stream URL',
    platform: 'Platform',
    videoId: 'Video or channel ID',
    recordingUrl: 'Recording URL (optional)',
    save: 'Save streaming settings',
    saving: 'Saving…',
    saveSuccess: 'Streaming settings saved',
    saveError: 'Failed to save streaming settings',
    joinStream: 'Join stream',
    createZoom: 'Create Zoom meeting',
    createGoogle: 'Create Google Meet',
    zoomCreated: 'Zoom meeting created',
    googleCreated: 'Google Meet created',
    meetingError: 'Failed to create meeting',
    integrationConnected: 'Your account is connected. Click to generate a meeting link.',
    integrationRequired: 'Connect your account in integration settings first.',
    connected: 'Connected',
    notConnected: 'Not connected',
    connect: 'Connect',
    disconnect: 'Disconnect',
    zoomConnected: 'Zoom account connected',
    googleConnected: 'Google account connected',
    zoomConnectError: 'Failed to connect Zoom',
    googleConnectError: 'Failed to connect Google',
    integrationsTitle: 'Integrations',
    integrationsDesc: 'Connect Zoom or Google to auto-generate meeting links.',
  },

  ticketTransfer: {
    title: 'Transfer Ticket',
    recipientLabel: 'Recipient email or wallet address',
    recipientPlaceholder: 'email@example.com or Solana wallet',
    messageLabel: 'Message (optional)',
    messagePlaceholder: 'Add a personal note for the recipient…',
    nftWarning: 'This ticket has an NFT. The NFT will be transferred to the recipient.',
    transferButton: 'Transfer Ticket',
    cancelButton: 'Cancel Transfer',
    statusPending: 'Pending transfer to {{email}} — expires {{date}}',
    statusTransferred: 'Transferred to {{name}} on {{date}}',
    statusReceived: 'You received this ticket from {{name}}',
    nftTransferPending: 'NFT transfer pending…',
    claimTitle: 'Claim Your Ticket',
    claimButton: 'Claim Ticket',
    claimSuccess: 'Ticket claimed successfully!',
    claimExpired: 'This transfer link has expired.',
    claimAlreadyClaimed: 'This ticket has already been claimed.',
    claimWrongAccount: 'This transfer was sent to a different account.',
    signInPrompt: 'Sign in or create an account to claim your ticket.',
    claimLoading: 'Checking your ticket…',
    claimFailed: 'Failed to claim ticket',
    viewMyTickets: 'View My Tickets',
    transferSuccessDirect: 'Ticket transferred to {{name}}',
    transferSuccessPending: 'Transfer pending — claim email sent',
    transferFailed: 'Transfer failed',
    cancelSuccess: 'Transfer cancelled',
    cancelFailed: 'Failed to cancel transfer',
    recipient_required: 'Provide recipient email or wallet',
    invalid_recipient: 'Enter a valid email or Solana wallet address',
    invalid_email: 'Invalid email format',
    invalid_wallet: 'Invalid wallet address',
    message_too_long: 'Message must be under 500 characters',
  },

  // ─── Loyalty points ───────────────────────────────────────────
  loyalty: {
    balance: {
      title: 'Your',
      titleHighlight: 'Points',
      pointsLabel: 'points',
      totalEarned: 'Total earned',
      totalRedeemed: 'Total redeemed',
      loginStreak: 'Login streak',
      streakDays: '{{count}} days',
    },
    points: {
      title: 'Points',
      subtitle: 'Earn points by participating in the Creatix ecosystem.',
      historyTitle: 'History',
      rewardsLink: 'Rewards',
      leaderboardLink: 'Leaderboard',
      balanceError: 'Failed to load balance. Please try again.',
      historyError: 'Failed to load history. Please try again.',
      emptyHistory: 'No transactions yet.',
      loadMore: 'Load more',
      loading: 'Loading…',
      filterTypeAll: 'All types',
      filterTypeEarn: 'Earned',
      filterTypeRedeem: 'Redeemed',
      filterSourceAll: 'All sources',
      filterType: 'Type',
      filterSource: 'Source',
    },
    sources: {
      check_in: 'Check-in',
      rsvp: 'RSVP',
      review: 'Review',
      event_create: 'Event created',
      community_host: 'Community host',
      daily_login: 'Daily login',
      redemption: 'Redemption',
    },
    rewards: {
      title: 'Rewards',
      subtitleLoading: 'Loading balance…',
      subtitleAvailable: 'You have {{count}} points available',
      subtitleSignIn: 'Sign in to redeem rewards',
      myPointsLink: 'My points',
      catalogTab: 'Catalog',
      historyTab: 'My redemptions',
      loadError: 'Failed to load rewards. Please try again.',
      redeemSuccess: 'Reward redeemed successfully!',
      redeemFailed: 'Failed to redeem reward',
      emptyCatalog: 'No rewards available right now.',
      emptyRedemptions: 'No redemptions yet.',
      confirmTitle: 'Confirm redemption',
      confirmDescription:
        'Redeem {{name}} for {{cost}} points? Your balance will be reduced to {{remaining}} points.',
      cancel: 'Cancel',
      confirm: 'Confirm',
      redeeming: 'Redeeming…',
      redeem: 'Redeem',
      insufficient: 'Insufficient points',
      unavailable: 'Unavailable',
      soldOut: 'Sold out',
      remaining: '{{count}} remaining',
      costPts: '{{cost}} pts',
      spentPts: '-{{cost}} pts',
    },
    redemptionStatus: {
      active: 'Active',
      used: 'Used',
      expired: 'Expired',
      refunded: 'Refunded',
    },
    leaderboard: {
      title: 'Leaderboard',
      subtitle: 'Top community members by points earned.',
      myRank: 'Your rank: #{{rank}} ({{points}} pts)',
      myPointsLink: 'My points',
      loadError: 'Failed to load leaderboard. Please try again.',
      empty: 'No leaderboard data yet.',
      topEarners: 'Top earners',
      periodAll: 'All time',
      periodMonthly: 'Monthly',
      periodWeekly: 'Weekly',
      you: 'You',
    },
    levels: {
      bronze: 'Bronze',
      silver: 'Silver',
      gold: 'Gold',
      platinum: 'Platinum',
    },
  },

  // ─── Language ─────────────────────────────────────────────────
  language: {
    label: 'Language',
    english: 'English',
    indonesian: 'Indonesian',
    switchTo: 'Switch to {{lang}}',
    switchEn: '🇬🇧 EN',
    switchId: '🇮🇩 ID',
  },

  // ─── Social sharing ───────────────────────────────────────────
  share: {
    title: 'Share',
    copyLink: 'Copy Link',
    linkCopied: 'Link copied to clipboard',
    embedWidget: 'Embed Widget',
    copyEmbedCode: 'Copy Embed Code',
    embedCodeCopied: 'Embed code copied',
  },
} as const

type DeepStringRecord = {
  [key: string]: string | readonly string[] | DeepStringRecord
}

export type Messages = DeepStringRecord
